import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign,
  TrendingUp,
  Users,
  CheckCircle,
  Clock,
  AlertTriangle,
  AlertCircle,
  BarChart3,
  PieChart,
  Send,
  Sparkles,
  ArrowUp,
  ArrowDown,
  Calendar,
  Target,
  X,
  Phone,
  MessageCircle,
  Timer,
  History,
  User,
  ExternalLink,
  PlusCircle,
  Edit,
  Trash2,
  FileText,
  Sun,
  Moon,
  ChevronRight,
  ChevronDown,
  MapPin,
  Banknote,
  Contact,
  ClipboardCheck
} from 'lucide-react';
import { getWhatsappLink, formatDate } from '../../utils/helpers';
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { STAGE_COLORS, COLORS, LOSS_REASONS, LOSS_REASON_BY_ID, DISQUALIFYING_REASONS, PREQUALIFICATION_STATUSES, AWARD_STATUSES } from '../../lib/constants';
import { safeAmount, formatCurrency, isWonStage } from '../../utils/helpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSocket } from '../../hooks/useSocket';
import { auditService, bookingService, conversationService, contractsService, settingsService, approvalService, fetchWithAuth, API_BASE_URL } from '../../services/api';
import { Skeleton, DatePicker, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';



// Module-level cache to prevent flashing loading states on re-mounts
const cachedFinancialsMap = new Map();
// Full banquet booking history (Demand, YoY, event-axis money) lives in
// booking.service — Finance reads the same rows, and the two screens have to
// agree row for row when you click through from a money card.
// §4 ad-spend ledger — cached per app session, written back via settings API.
let cachedAdSpend = null;

// §4 spend ledger editor. Meta's column is read-only — the sync owns it;
// humans type only the money Meta cannot see: Google, one-offs, and the fee.
function SpendEditor({ open, onClose, ledger, onSaved }) {
  const { t } = useLanguage();
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) {
      setNote('');
      setDraft(JSON.parse(JSON.stringify(ledger || { months: {} })));
    }
  }, [open, ledger]);
  if (!open || !draft) return null;

  const now = new Date();
  const keys = Array.from({ length: 13 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const setVal = (m, src, v) => {
    const months = { ...(draft.months || {}) };
    months[m] = { ...(months[m] || {}) };
    const num = Number(v);
    if (!v || isNaN(num) || num <= 0) delete months[m][src];
    else months[m][src] = Math.round(num);
    setDraft({ ...draft, months });
  };
  const save = async () => {
    setBusy(true);
    try {
      const saved = await settingsService.update('ad_spend', draft);
      onSaved(saved);
      onClose();
    } catch (e) { setNote(e.message || 'Save failed'); }
    setBusy(false);
  };
  const syncMeta = async () => {
    setBusy(true);
    setNote('');
    try {
      const r = await fetchWithAuth(`${API_BASE_URL}/marketing/sync-meta-spend`, { method: 'POST' });
      if (r.status === 404) {
        setNote(t('syncNotDeployed') || 'Sync endpoint not deployed yet — one click after the next deploy.');
      } else {
        const j = await r.json();
        if (!r.ok || j.status === false) {
          setNote(j.message || 'Sync failed');
        } else {
          const fresh = await settingsService.get('ad_spend');
          onSaved(fresh);
          setDraft(JSON.parse(JSON.stringify(fresh || { months: {} })));
          setNote(j.message || 'Synced');
        }
      }
    } catch (e) { setNote(e.message || 'Sync failed'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-bold text-gray-900">{t('spendLedger') || 'Ad Spend Ledger'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">Ã—</button>
        </div>
        <div className="p-4 overflow-y-auto space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs font-bold text-gray-500 uppercase">{t('agencyFee') || 'Agency fee / month'}</label>
            <input
              type="number"
              className="border rounded-lg p-2 text-sm w-36 text-right"
              value={draft.agencyFeeMonthly || ''}
              placeholder="150000"
              onChange={(e) => setDraft({ ...draft, agencyFeeMonthly: Number(e.target.value) || 0 })}
            />
            <button onClick={syncMeta} disabled={busy} className="ml-auto px-3 py-2 text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors">
              {t('syncMeta') || 'Sync Meta spend now'}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-2 py-2 text-xs font-semibold text-gray-600 uppercase">{t('month') || 'Month'}</th>
                  <th className="px-2 py-2 text-xs font-semibold text-gray-600 uppercase text-right">Meta (auto)</th>
                  <th className="px-2 py-2 text-xs font-semibold text-gray-600 uppercase text-right">Google Ads</th>
                  <th className="px-2 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('otherSpend') || 'Other'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {keys.map((m) => (
                  <tr key={m}>
                    <td className="px-2 py-1.5 font-medium text-gray-700">{m}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">
                      {draft.months?.[m]?.['Meta Prequalification Gen'] ? Number(draft.months[m]['Meta Prequalification Gen']).toLocaleString() : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input type="number" className="border rounded p-1 text-right text-sm w-28" value={draft.months?.[m]?.['Google Ads'] ?? ''} onChange={(e) => setVal(m, 'Google Ads', e.target.value)} />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input type="number" className="border rounded p-1 text-right text-sm w-28" value={draft.months?.[m]?.['Other'] ?? ''} onChange={(e) => setVal(m, 'Other', e.target.value)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {note && <p className="text-xs text-amber-700">{note}</p>}
          <p className="text-[11px] text-gray-400">
            {t('spendLedgerNote') || 'Meta fills itself via sync. Type only what Meta cannot see: Google, one-off channels, and the fee.'}
          </p>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">{t('cancel') || 'Cancel'}</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
            {t('save') || 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OwnerDashboard({ prequalifications = [], tenders = [], users = [], profiles = [], profilesLoading = false, profilesPagination = null, currentUser = null, onShowRevenue, fetchAnalytics, analyticsLoading, leadsLoading }) {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { socket } = useSocket();

  // Approvals Quick Stats (CEO & GM Operations)
  const isApproverUser = ['CEO', 'GM_Operations', 'BM_Operations'].includes(currentUser?.role);
  const [approvalCounts, setApprovalCounts] = useState({
    totalPending: 0,
    pendingProfiles: 0,
    pendingPrequalifications: 0,
    pendingTenders: 0,
  });
  const [approvalCountsLoading, setApprovalCountsLoading] = useState(false);

  const fetchApprovalCounts = useCallback(async () => {
    if (!isApproverUser) return;
    try {
      setApprovalCountsLoading(true);
      const res = await approvalService.getCounts();
      if (res?.status && res.data) {
        setApprovalCounts(res.data);
      }
    } catch (err) {
      console.error('Failed to load approval counts on OwnerDashboard:', err);
    } finally {
      setApprovalCountsLoading(false);
    }
  }, [isApproverUser]);

  useEffect(() => {
    fetchApprovalCounts();
  }, [fetchApprovalCounts]);

  // Live Socket.IO listener for real-time counts update
  useEffect(() => {
    if (!socket || !isApproverUser) return;
    const handleUpdate = () => {
      fetchApprovalCounts();
    };
    socket.on('approval_counts_updated', handleUpdate);
    socket.on('notification', handleUpdate);
    return () => {
      socket.off('approval_counts_updated', handleUpdate);
      socket.off('notification', handleUpdate);
    };
  }, [socket, isApproverUser, fetchApprovalCounts]);

  // Rolling windows, not calendar-to-date: "This Month" on the 2nd of a month
  // is a 48-hour window and made every card read zero. Default is the last 30
  // days — the question the dashboard actually opens to c
  const [dateRange, setDateRange] = useState('30d');
  const fmtDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [customFrom, setCustomFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 29); return fmtDay(d); });
  const [customTo, setCustomTo] = useState(() => fmtDay(new Date()));
  const [typeFilter, setTypeFilter] = useState('Prequalification');
  const [dashboardLeads, setDashboardLeads] = useState(() => {
    const leads = prequalifications || [];
    const tnds = (tenders || []).map(t => ({ ...t, stage: t.status, type: t.type || 'tender' }));
    const profs = (profiles || []).map(p => ({ ...p, stage: p.status || 'New', type: 'profile', uiType: 'Profile' }));
    return [...leads, ...tnds, ...profs];
  });
  const [analyticsData, setAnalyticsData] = useState(null);
  const [lossViewType, setLossViewType] = useState('prequalification');

  const [banquetFinancials, setBanquetFinancials] = useState({ booked: 0, collected: 0, outstanding: 0, count: 0 });
  const [finLoading, setFinLoading] = useState(false);
  const [revenueAxis, setRevenueAxis] = useState('booking');

  const [stageBreakdown, setStageBreakdown] = useState([]);

  // --- Contracts logic ---
  const [allContracts, setAllContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);

  useEffect(() => {
    const fetchContracts = async () => {
      setContractsLoading(true);
      try {
        const result = await contractsService.getAll({});
        if (result?.status && Array.isArray(result.data)) {
          setAllContracts(result.data);
        }
      } catch (err) {
        console.error('Error fetching contracts:', err);
      } finally {
        setContractsLoading(false);
      }
    };
    fetchContracts();
  }, []);

  const expiringContractsCount = useMemo(() => {
    const today = new Date();
    today.setHours(0,0,0,0);
    const next30 = new Date(today);
    next30.setDate(today.getDate() + 30);
    next30.setHours(23,59,59,999);
    return allContracts.filter(c => {
      if (c.status !== 'Active' || !c.endDate) return false;
      const end = new Date(c.endDate);
      return end >= today && end <= next30;
    }).length;
  }, [allContracts]);

  const expiredContractsCount = useMemo(() => {
    return allContracts.filter(c => c.status === 'Expired').length;
  }, [allContracts]);

  const [banquetAllRows, setBanquetAllRows] = useState([]);
  const [allRowsLoading, setAllRowsLoading] = useState(false);
  useEffect(() => {
    bookingService.getAll({ limit: 1000 }).then(res => setBanquetAllRows(res?.data || [])).finally(() => setAllRowsLoading(false));
  }, []);

  // Lifted to component scope: used both by the financials fetch below and by
  // the Finance drill-down links on the money cards, so the list you land on
  // covers exactly the period the card was showing.
  const getDateRangeBounds = (range) => {
    const now = new Date();
    let from, to;
    switch (range) {
      case '90d': {
        from = new Date(now);
        from.setDate(from.getDate() - 89);
        to = new Date(now);
        break;
      }
      case 'ytd': {
        from = new Date(now.getFullYear(), 0, 1);
        to = new Date(now);
        break;
      }
      case '12m': {
        from = new Date(now);
        from.setDate(from.getDate() - 364);
        to = new Date(now);
        break;
      }
      case 'all': {
        from = new Date(2000, 0, 1);
        to = new Date(now);
        break;
      }
      case 'custom': {
        // The pickers own these strings; fall back to last-30-days shape if
        // one is cleared mid-edit so nothing downstream sees an empty bound.
        if (customFrom && customTo) return { from: customFrom, to: customTo };
        from = new Date(now);
        from.setDate(from.getDate() - 29);
        to = new Date(now);
        break;
      }
      default: { // 30d
        from = new Date(now);
        from.setDate(from.getDate() - 29);
        to = new Date(now);
      }
    }
    return { from: fmtDay(from), to: fmtDay(to) };
  };

  // Money cards open the Finance screen scoped to the same period and status,
  // rather than duplicating that table in a modal. Finance already has search,
  // sorting, amount filters and pagination.
  //
  // The axis travels with the link: a card reading "outstanding for these
  // events" has to land on the list of those events, not on bookings that
  // happened to be sold in the same weeks. Callers pinned to one axis pass it
  // explicitly; the money cards inherit whichever toggle is showing.
  const openInFinance = (status, axis = revenueAxis) => {
    const { from, to } = getDateRangeBounds(dateRange);
    navigate(`/contracts?from=${from}&to=${to}&status=${status}&axis=${axis}`);
  };

  useEffect(() => {

    const fetchFinancials = async () => {
      try {
        const { from, to } = getDateRangeBounds(dateRange);
        const cacheKey = `${from}_${to}`;
        // A warm cache means real numbers are on screen already — keep them and
        // refresh underneath rather than flashing a skeleton over good data.
        if (cachedFinancialsMap.has(cacheKey)) {
          setBanquetFinancials(cachedFinancialsMap.get(cacheKey));
          setFinLoading(false);
        } else {
          setFinLoading(true);
        }
        const result = await contractsService.getAll({ from, to, dateAxis: 'created' });
        if (result?.status && Array.isArray(result.data)) {
          let booked = 0;
          let collected = 0;
          let outstanding = 0;
          result.data.forEach(l => {
            const financialSummary = l.financial_summary || {};
            const totalValue = safeAmount(financialSummary.invoice_net_amount || 0);
            const totalPaid = safeAmount(financialSummary.total_received_amount || 0);
            const totalDue = safeAmount(financialSummary.balance_amount ?? Math.max(0, totalValue - totalPaid));
            booked += totalValue;
            collected += totalPaid;
            outstanding += totalDue;
          });
          // count: the BMS returns bookings PLACED in the requested window
          // (evidence: bookings for 2027 events come back when to_date is
          // mid-2026, so the API filters on order_date, not event date). Row
          // count therefore IS "bookings taken this period" — complete,
          // including walk-ins and direct BMS entries that never had a CRM prequalification.
          const newVals = { booked, collected, outstanding, count: result.data.length };
          cachedFinancialsMap.set(cacheKey, newVals);
          setBanquetFinancials(newVals);
        }
      } catch (err) {
        console.error('Error fetching banquet financials:', err);
      } finally {
        setFinLoading(false);
      }
    };
    fetchFinancials();
  }, [dateRange, customFrom, customTo]);

  // Event-axis money: the same three figures keyed on function_date instead of
  // order_date — "what's on the books FOR this period's events, whenever it was
  // sold". Computed from the full booking book already cached for the Demand
  // and YoY cards (bookings placed 2020→today, which by definition contains
  // every future event ever sold), so flipping the toggle costs no API call.
  const eventFinancials = useMemo(() => {
    const { from, to } = getDateRangeBounds(dateRange);
    let booked = 0, collected = 0, outstanding = 0, count = 0;

    return { booked, collected, outstanding, count };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banquetAllRows, dateRange, customFrom, customTo]);
  const fin = revenueAxis === 'event' ? eventFinancials : banquetFinancials;


  const [activeModal, setActiveModal] = useState(null); // 'prequalifications', 'conversion', 'stale', 'ytd', 'source', 'funnel', 'summary'
  const [selectedSource, setSelectedSource] = useState(null);
  // §3 month-trend source selector (Marketing Performance card)
  const [marketingSource, setMarketingSource] = useState('Meta Prequalification Gen');
  // §4 spend ledger + editor; §5 year-on-year month selector (December default)
  const [adSpend, setAdSpend] = useState(cachedAdSpend);
  const [showSpendEditor, setShowSpendEditor] = useState(false);
  useEffect(() => {
    if (cachedAdSpend) return;
    settingsService.get('ad_spend')
      .then((v) => { cachedAdSpend = v || { months: {} }; setAdSpend(cachedAdSpend); })
      .catch(() => { cachedAdSpend = { months: {} }; setAdSpend(cachedAdSpend); });
  }, []);
  const [selectedStage, setSelectedStage] = useState(null);
  const [visibleCount, setVisibleCount] = useState(50);
  const [summaryModalType, setSummaryModalType] = useState(null);
  const [selectedLeadDetail, setSelectedLeadDetail] = useState(null);
  const [visibleTrendLines, setVisibleTrendLines] = useState(['prequalifications', 'enlisted', 'tender', 'booked']);

  // ── Sales Team date filter ─────────────────────────────────────────
  const getMonthDefaults = () => {
    const now = new Date();
    const y = now.getFullYear(), m = String(now.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return {
      from: `${y}-${m}-01`,
      to: `${y}-${m}-${String(lastDay).padStart(2, '0')}`,
    };
  };
  const monthDef = getMonthDefaults();
  const [summaryFrom, setSummaryFrom] = useState(monthDef.from);
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [summaryTo, setSummaryTo] = useState(monthDef.to);

  // ── Role-based hierarchy for the team summary table ──────────────────────
  // Track which BM/TL sections are expanded (CEO and BM view)
  // Track all rendered section keys so we can seed the Set when first collapsing from 'all'
  const sectionKeysRef = React.useRef(new Set());

  const [expandedSections, setExpandedSections] = React.useState(new Set(['all']));

  const toggleSection = (key) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has('all')) {
        // First individual toggle: seed all known keys as expanded, then collapse the clicked one
        sectionKeysRef.current.forEach(k => next.add(k));
        next.delete('all');
        next.delete(key);
      } else {
        if (next.has(key)) next.delete(key);
        else next.add(key);
      }
      return next;
    });
  };

  // Build hierarchy tree from users array using parentId
  const hierarchy = useMemo(() => {
    const role = currentUser?.role;
    const myId = currentUser?.id;
    if (!role) return { type: 'unknown' };

    if (role === 'Team_Lead' || role === 'Senior_Team_Lead') {
      const associates = (users || []).filter(u => ['Associate_Team_Lead', 'Associate'].includes(u.role) && u.parentId === myId && u.is_active !== false);
      return { type: 'team_lead', associates };
    }

    if (role === 'GM_Operations' || role === 'BM_Operations') {
      const teamLeads = (users || []).filter(u => ['Team_Lead', 'Senior_Team_Lead'].includes(u.role) && u.parentId === myId && u.is_active !== false);
      return {
        type: 'bm',
        teamLeads: teamLeads.map(tl => ({
          ...tl,
          associates: (users || []).filter(u => ['Associate_Team_Lead', 'Associate'].includes(u.role) && u.parentId === tl.id && u.is_active !== false)
        }))
      };
    }

    // CEO — sees all
    const bms = (users || []).filter(u => ['GM_Operations', 'BM_Operations'].includes(u.role) && u.is_active !== false);
    const bmIds = new Set(bms.map(b => b.id));

    const orphanedTLs = (users || []).filter(u => ['Team_Lead', 'Senior_Team_Lead'].includes(u.role) && (!u.parentId || !bmIds.has(u.parentId)) && u.is_active !== false);

    return {
      type: 'ceo',
      bms: bms.map(bm => {
        const tls = (users || []).filter(u => ['Team_Lead', 'Senior_Team_Lead'].includes(u.role) && u.parentId === bm.id && u.is_active !== false);
        return {
          ...bm,
          teamLeads: tls.map(tl => ({
            ...tl,
            associates: (users || []).filter(u => ['Associate_Team_Lead', 'Associate'].includes(u.role) && u.parentId === tl.id && u.is_active !== false)
          }))
        };
      }),
      orphanedTLs: orphanedTLs.map(tl => ({
        ...tl,
        associates: (users || []).filter(u => ['Associate_Team_Lead', 'Associate'].includes(u.role) && u.parentId === tl.id && u.is_active !== false)
      }))
    };
  }, [users, currentUser]);

  // Fetch analytics from the scalable API whenever dateRange changes
  useEffect(() => {
    if (fetchAnalytics) {
      // Send explicit bounds: the analytics API only knows the legacy preset
      // names, but it honours startDate/endDate for any window. 'all' keeps
      // its dedicated server path (year-bucketed trend, no previous period).
      const { from, to } = getDateRangeBounds(dateRange);
      const params = dateRange === 'all' ? { dateRange } : { dateRange, startDate: from, endDate: to };
      fetchAnalytics(params).then(data => {
        if (data) setAnalyticsData(data);
      });
    }
  }, [dateRange, customFrom, customTo, fetchAnalytics]);

  // Keep dashboardLeads in sync with props for drilldown modals
  useEffect(() => {
    const leads = prequalifications || [];
    const tnds = (tenders || []).map(t => ({ ...t, stage: t.status, type: t.type || 'tender' }));
    const profs = (profiles || []).map(p => ({ ...p, stage: p.status || 'New', type: 'profile', uiType: 'Profile' }));
    if (leads.length > 0 || tnds.length > 0 || profs.length > 0) {
      setDashboardLeads([...leads, ...tnds, ...profs]);
    }
  }, [prequalifications, tenders, profiles]);

  // Fetch initial data & setup socket listeners
  useEffect(() => {
    let isMounted = true;

    const fetchInitialData = () => {
      // Background fetch without showing loading state if clicked back

      // No separate prequalifications fetch — dashboardLeads is synced from the `prequalifications` prop (App.jsx usePrequalifications hook)
      // This avoids a redundant 13MB+ API call on every dashboard visit.
    };

    fetchInitialData();

    return () => {
      isMounted = false;
    };
  }, []);


  // Map analytics API data to the shape expected by dashboard UI
  // All heavy computation is now done server-side in getLeadsAnalytics controller
  const metrics = useMemo(() => {
    const a = analyticsData;
    if (!a) {
      // Default empty state while loading
      return {
        totalLeads: 0, periodLeads: 0, activePeriodLeads: 0, bookedPeriodLeads: 0,
        cohortBookedLeads: 0,
        previousPeriodLeads: 0, leadsBySource: {}, conversionBySource: {}, leadsByStage: {},
        employeeStats: {}, revenueInPeriod: 0, previousRevenue: 0, ytdRevenue: 0,
        conversionRate: 0, conversionMaturing: false, trendData: [], staleLeads: [],
        overduePayments: [], avgResponseTime: null,
        revenueGrowth: 0, _startOfPeriod: new Date(0)
      };
    }
    // The new fields land with the server half of this change. Until then —
    // and on any deploy that ships the bundle first — derive the same answers
    // from the period drill-down list, which has always carried every prequalification's
    // stage. Never fall back to the old arithmetic: subtracting one filtered
    // count from another is the bug being fixed, not a safe default.
    const dd = a.drilldownLeads || [];
    const ddActive = dd.filter((l) => !isWonStage(l.stage) && l.stage !== 'Lost' && l.stage !== 'Disqualified').length;
    const ddEnlisted = dd.filter((l) => l.stage === 'Enlisted').length;
    const ddBooked = dd.filter((l) => isWonStage(l.stage)).length;
    const cohortBooked = a.cohortBookedLeads ?? ddBooked;

    return {
      totalLeads: a.totalLeads || 0,
      periodLeads: a.periodLeads || 0,
      // Not won and not lost.
      activePeriodLeads: a.activePeriodLeads ?? ddActive,
      enlistedPeriodLeads: a.enlistedPeriodLeads ?? ddEnlisted,
      bookedPeriodLeads: a.bookedPeriodLeads || 0,
      cohortBookedLeads: cohortBooked,
      previousPeriodLeads: a.previousPeriodLeads || 0,
      leadsBySource: a.leadsBySource || {},
      conversionBySource: a.conversionBySource || {},
      leadsByStage: a.leadsByStage || {},
      employeeStats: a.employeeStats || {},
      revenueInPeriod: a.revenueInPeriod || 0,
      previousRevenue: a.previousRevenue || 0,
      ytdRevenue: a.ytdRevenue || 0,
      // Same cohort on both sides of the division, whichever half is deployed.
      conversionRate: a.cohortBookedLeads !== undefined
        ? (a.conversionRate || 0)
        : (a.periodLeads ? ((ddBooked / a.periodLeads) * 100).toFixed(1) : 0),
      conversionMaturing: a.conversionMaturing ?? true,
      trendData: a.trendData || [],
      staleLeads: a.staleLeads || [],
      drilldownLeads: a.drilldownLeads || [],
      enlistedDrilldownLeads: a.enlistedDrilldownLeads || [],
      tenderDrilldownLeads: a.tenderDrilldownLeads || [],
      totalTenders: a.totalTendersAll ?? (a.tenderDrilldownLeads || []).length,
      activeTenders: a.activeTendersAll ?? (a.tenderDrilldownLeads || []).filter(t => t.stage !== 'Won' && t.stage !== 'Lost').length,
      wonTenders: a.wonTendersAll ?? (a.tenderDrilldownLeads || []).filter(t => t.stage === 'Won').length,
      totalLeadsAll: a.totalLeadsAll ?? a.totalLeads ?? 0,
      enlistedLeadsAll: a.enlistedLeadsAll ?? 0,
      activeLeadsAll: a.activeTendersAll ?? 0,
      pipelineDrilldownLeads: a.pipelineDrilldownLeads || [],
      ytdDrilldownLeads: a.ytdDrilldownLeads || [],
      bookedDrilldownLeads: a.bookedDrilldownLeads || [],
      overduePayments: [],
      avgResponseTime: a.avgResponseTime !== undefined ? a.avgResponseTime : null,
      revenueGrowth: a.revenueGrowth || 0,
      _startOfPeriod: dateRange === 'all'
        ? new Date(0)
        : new Date(getDateRangeBounds(dateRange).from + 'T00:00:00')
    };
  }, [analyticsData, dateRange, customFrom, customTo]);

  // Headline numbers wait for their data. `analyticsData` is kept as the test
  // rather than the loading flag alone, so a background refresh of an already
  // populated card doesn't blank it.
  const kpiLoading = analyticsLoading && !analyticsData;
  // Money cards read the banquet system on the booking axis and the cached
  // all-rows dump on the event axis — different fetches, different waits.
  const moneyLoading = revenueAxis === 'event' ? allRowsLoading : finLoading;
  // The whole-prequalification fetch is the slowest thing on the page, and the cards it
  // feeds (Demand, Marketing, Why we lose) render *empty states* while it runs
  // — "No prequalifications in this period" is a confident wrong answer, not a wait.
  const leadsPending = leadsLoading && !(dashboardLeads || []).length;
  const CardLoading = ({ rows = 3 }) => (
    <div className="space-y-2 py-3">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} width={`${92 - i * 12}%`} height={14} animation="wave" />
      ))}
    </div>
  );
  const StatValue = ({ loading, children, className = '' }) => (
    loading
      ? <div className="mt-1 h-7 flex items-center"><Skeleton width="65%" height={18} animation="wave" /></div>
      : <h3 className={`text-lg font-bold text-gray-900 mt-1 truncate ${className}`}>{children}</h3>
  );

  // One list, two renderings — the desktop strip and the phone dropdown must
  // never drift apart on which windows exist or what they're called.
  const DATE_RANGES = [
    ['30d', t('last30Days') || '30 Days'],
    ['90d', t('last90Days') || '90 Days'],
    ['ytd', t('ytdLabel') || 'YTD'],
    ['12m', t('last12Months') || '12 Months'],
    ['all', t('allTime') || 'All'],
    ['custom', t('customRange') || 'Custom'],
  ];



  // Prepare chart data
  const filteredTenders = useMemo(() => {
    const { from: periodFrom, to: periodTo } = getDateRangeBounds(dateRange);
    return (tenders || []).filter(t => {
      const created = String(t.createdAt || '').slice(0, 10);
      return created && created >= periodFrom && created <= periodTo;
    });
  }, [tenders, dateRange, customFrom, customTo]);

  const sourceData = useMemo(() => {
    const dataMap = {};
    if (typeFilter === 'All' || typeFilter === 'Prequalification') {
      Object.entries(metrics.leadsBySource).forEach(([name, val]) => {
        dataMap[name] = (dataMap[name] || 0) + val;
      });
    }
    if (typeFilter === 'All' || typeFilter === 'Tender/Proposal') {
      filteredTenders.forEach(t => {
        const name = typeof t.source === 'object' ? t.source?.name : t.source;
        const profileSource = typeof t.profile?.source === 'object' ? t.profile?.source?.name : t.profile?.source;
        const srcName = name || profileSource || 'Direct / Unknown';
        dataMap[srcName] = (dataMap[srcName] || 0) + 1;
      });
    }
    return Object.entries(dataMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [metrics.leadsBySource, filteredTenders, typeFilter]);

  // Denominator for the source percentages based on the current filtered sources
  const periodSourceTotal = useMemo(() => {
    return (sourceData || []).reduce((sum, entry) => sum + (entry.value || 0), 0);
  }, [sourceData]);

  // A tentative booking is created when a prequalification reaches Site Visited (A-03/A-06).
  //
  // Counting prequalifications *currently* at that stage undercounts badly — a tentative
  // that went on to Won or Lost stops being counted, and most of them do. The
  // `siteVisited` flag records that a visit happened and survives the prequalification
  // moving on, so it answers the actual question: how many tentative bookings
  // were made. (Some prequalifications sit at Site Visited without the flag, so accept
  // either signal.) Scoped by prequalification creation date to match the cards above.
  //
  // Walk-ins are excluded: their flag is set at birth (the inquiry happened
  // inside the hall), but browsing in off the street is not a tentative
  // booking — counting them would drown the card in foot traffic.
  const { from: periodFrom, to: periodTo } = getDateRangeBounds(dateRange);
  const tentativeLeads = (dashboardLeads || []).filter((l) => {
    if (/walk/i.test(String(l.source || ''))) return false;
    const created = String(l.inquiryDate || l.createdAt || '').slice(0, 10);
    if (!created || created < periodFrom || created > periodTo) return false;
    return l.siteVisited === true || l.stage === 'Site Visited';
  });

  // ── Demand view (§6): of the inquiries received in the selected period,
  // which event months are they asking about? Confirmed banquet bookings for
  // the same months sit alongside, so demand reads against supply. Prequalifications
  // without a usable event date are counted and disclosed, never silently
  // dropped; a horizon keeps junk dates (2001, 2030) from stretching the axis.
  const demand = useMemo(() => {
    const inPeriod = (dashboardLeads || []).filter((l) => {
      const created = String(l.inquiryDate || l.createdAt || '').slice(0, 10);
      return created && created >= periodFrom && created <= periodTo;
    });

    const now = new Date();
    const minKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const maxD = new Date(now.getFullYear(), now.getMonth() + 18, 1);
    const maxKey = `${maxD.getFullYear()}-${String(maxD.getMonth() + 1).padStart(2, '0')}`;

    let noDate = 0;
    let outsideHorizon = 0;
    const byMonth = {};
    inPeriod.forEach((l) => {
      const raw = String(l.eventDate || '');
      const key = /^\d{4}-\d{2}/.test(raw) ? raw.slice(0, 7) : '';
      if (!key) { noDate += 1; return; }
      if (key < minKey || key > maxKey) { outsideHorizon += 1; return; }
      byMonth[key] = (byMonth[key] || 0) + 1;
    });

    const bookedByMonth = {};
    (banquetAllRows || []).forEach((b) => {
      const key = String(b.function_date || '').slice(0, 7);
      if (key) bookedByMonth[key] = (bookedByMonth[key] || 0) + 1;
    });

    const monthLabel = (key) => {
      const [y, m] = key.split('-').map(Number);
      return new Date(y, m - 1, 1).toLocaleString(language === 'ur' ? 'ur-PK' : 'en-GB', { month: 'short', year: '2-digit' });
    };

    const rows = Object.keys(byMonth).sort().map((key) => ({
      month: monthLabel(key),
      inquiries: byMonth[key],
      booked: bookedByMonth[key] || 0,
    }));

    return { rows, noDate, outsideHorizon, total: inPeriod.length };
  }, [dashboardLeads, periodFrom, periodTo, banquetAllRows, language]);

  // Open prequalifications carrying a quote — the records behind the Proposal Value figure.
  // Built server-side now: every live deal regardless of when it came in, so
  // the list matches a card that no longer moves with the date filter. Falls
  // back to the period-scoped list if only the bundle has deployed.
  const pipelineLeads = (metrics.pipelineDrilldownLeads || []).length
    ? metrics.pipelineDrilldownLeads
    : (metrics.drilldownLeads || [])
      .filter((l) => !isWonStage(l.stage) && l.stage !== 'Lost' && safeAmount(l.amount) > 0)
      .sort((a, b) => safeAmount(b.amount) - safeAmount(a.amount));

  // Helper: normalize source string same way metrics does
  const normalizeSource = (src) => {
    if (!src) return 'Unknown';
    const trimmed = src.trim();
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  };

  const pct = (n, d) => (d ? ((n / d) * 100).toFixed(1).replace(/\.0$/, '') + '%' : '—');

  // ── Marketing Performance (§1–3 of the marketing spec) ──────────────────
  // Cohort semantics throughout: prequalifications CREATED in the window; site visits and
  // bookings are counted on those same prequalifications wherever they stand today. Visits
  // accept either signal (flag or current stage), matching the Tentative card,
  // so the dashboard never shows two different answers to the same question.
  const marketing = useMemo(() => {
    const visited = (l) => l.siteVisited === true || l.stage === 'Site Visited';
    const inPeriod = (dashboardLeads || []).filter((l) => {
      const created = String(l.inquiryDate || l.createdAt || '').slice(0, 10);
      return created && created >= periodFrom && created <= periodTo;
    });

    // §1 — per-source table for the selected period
    const bySource = {};
    inPeriod.forEach((l) => {
      const name = l.source || 'Unknown';
      const b = (bySource[name] = bySource[name] || { name, prequalifications: 0, visits: 0, booked: 0 });
      b.prequalifications += 1;
      if (visited(l)) b.visits += 1;
      if (isWonStage(l.stage)) b.booked += 1;
    });
    const sourceRows = Object.values(bySource).sort((a, b) => b.prequalifications - a.prequalifications);

    // §2 — Meta prequalifications grouped by ad set (names only exist since 29 Jun 2026)
    const byAdset = {};
    inPeriod
      .filter((l) => normalizeSource(l.source) === normalizeSource('Meta Prequalification Gen'))
      .forEach((l) => {
        const key = l.adsetName || l.meta?.adset_name;
        if (!key) return;
        const b = (byAdset[key] = byAdset[key] || { name: key, prequalifications: 0, visits: 0, booked: 0 });
        b.prequalifications += 1;
        if (visited(l)) b.visits += 1;
        if (isWonStage(l.stage)) b.booked += 1;
      });
    const adsetRows = Object.values(byAdset).sort((a, b) => b.prequalifications - a.prequalifications);

    // §3 — fixed last-12-months trend for one selectable source. Deliberately
    // independent of the period filter: its job is making a bad month visible
    // the week it happens (Feb and Jun 2026 produced zero bookings and nobody
    // knew), which a single-period view can never show.
    const now = new Date();
    const trendRows = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      trendRows.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleString(language === 'ur' ? 'ur-PK' : 'en-GB', { month: 'short', year: '2-digit' }),
        prequalifications: 0, visits: 0, booked: 0
      });
    }
    const bucketByKey = Object.fromEntries(trendRows.map((b) => [b.key, b]));
    (dashboardLeads || []).forEach((l) => {
      if (marketingSource !== 'All' && normalizeSource(l.source) !== normalizeSource(marketingSource)) return;
      const b = bucketByKey[String(l.inquiryDate || l.createdAt || '').slice(0, 7)];
      if (!b) return;
      b.prequalifications += 1;
      if (visited(l)) b.visits += 1;
      if (isWonStage(l.stage)) b.booked += 1;
    });

    // Selector options come from the whole dataset, not just the period —
    // you should be able to inspect a source's year even in a quiet week.
    const allSourceNames = [...new Set((dashboardLeads || []).map((l) => l.source).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));

    return { sourceRows, adsetRows, trendRows, allSourceNames, periodTotal: inPeriod.length };
  }, [dashboardLeads, periodFrom, periodTo, marketingSource, language]);

  // §4 — media spend mapped onto the source table's period. A monthly ledger
  // can't honestly answer a one-week window, so costs render only for
  // month-or-longer periods, summing the calendar months in range.
  const spendCosts = useMemo(() => {
    const months = adSpend?.months || {};
    const from = new Date(periodFrom + 'T00:00:00');
    const to = new Date(periodTo + 'T00:00:00');
    if ((to - from) / 86400000 < 25) return { active: false, bySource: {}, adsetBySet: {}, adsetLeads: {}, adsetConvos: {}, fee: 0, monthCount: 0 };
    const keys = [];
    const cur = new Date(from.getFullYear(), from.getMonth(), 1);
    while (cur <= to) {
      keys.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`);
      cur.setMonth(cur.getMonth() + 1);
    }
    const bySource = {};
    keys.forEach((m) => {
      Object.entries(months[m] || {}).forEach(([src, amt]) => {
        const v = Number(amt) || 0;
        if (v) bySource[src] = (bySource[src] || 0) + v;
      });
    });
    // Per-ad-set numbers over the same period months, all from Meta's ledger:
    // spend, Meta-counted prequalifications (complete history — no attribution-era gap),
    // and messaging conversations for Click-to-WhatsApp sets. Spend and
    // results share one window, so Rs/Prequalification divides like with like.
    const sumMap = (root, out) => {
      keys.forEach((m) => {
        Object.entries(root?.[m] || {}).forEach(([name, amt]) => {
          const v = Number(amt) || 0;
          if (v) out[name] = (out[name] || 0) + v;
        });
      });
      return out;
    };
    const adsetBySet = sumMap(adSpend?.adsetMonths, {});
    const adsetLeads = sumMap(adSpend?.adsetLeadsMonths, {});
    const adsetConvos = sumMap(adSpend?.adsetConvoMonths, {});
    return {
      active: Object.keys(bySource).length > 0,
      bySource,
      adsetBySet,
      adsetLeads,
      adsetConvos,
      fee: (Number(adSpend?.agencyFeeMonthly) || 0) * keys.length,
      monthCount: keys.length
    };
  }, [adSpend, periodFrom, periodTo]);

  // Ad sets with spend but no CRM prequalifications must still appear — the Rs 157k
  // Click-to-WhatsApp test produced messages, not prequalification-form prequalifications, and a
  // spend-only row is a finding, not a join casualty.
  const adsetDisplayRows = useMemo(() => {
    const byName = {};
    marketing.adsetRows.forEach((r) => { byName[r.name] = { ...r }; });
    [
      ...Object.keys(spendCosts.adsetBySet || {}),
      ...Object.keys(spendCosts.adsetLeads || {}),
      ...Object.keys(spendCosts.adsetConvos || {})
    ].forEach((name) => {
      if (!byName[name]) byName[name] = { name, prequalifications: 0, visits: 0, booked: 0 };
    });
    return Object.values(byName).sort((a, b) =>
      (spendCosts.adsetBySet[b.name] || 0) - (spendCosts.adsetBySet[a.name] || 0) || b.prequalifications - a.prequalifications);
  }, [marketing.adsetRows, spendCosts.adsetBySet, spendCosts.adsetLeads, spendCosts.adsetConvos]);

  // Ledger spend for one trend bucket: the selected source's figure, or the
  // month's total across sources when viewing All.
  const trendSpendFor = (monthKey) => {
    const m = adSpend?.months?.[monthKey];
    if (!m) return 0;
    if (marketingSource === 'All') return Object.values(m).reduce((s, v) => s + (Number(v) || 0), 0);
    const hit = Object.entries(m).find(([src]) => normalizeSource(src) === normalizeSource(marketingSource));
    return hit ? Number(hit[1]) || 0 : 0;
  };

  // Why we lose — the loss-reason breakdown for prequalifications created in the period,
  // plus the number the raw conversion rate hides: a win rate measured only
  // against deals that were ever real. Prequalifications lost as spam, wrong numbers or
  // idle browsers are excluded from the denominator, because no salesperson
  // could have won them.
  const lossAnalysis = useMemo(() => {
    const inPeriod = (dashboardLeads || []).filter((l) => {
      const created = String(l.inquiryDate || l.createdAt || '').slice(0, 10);
      return created && created >= periodFrom && created <= periodTo;
    });

    let lost = [];
    let won = [];
    let baseLeads = [];

    const isTenderItem = (l) => l.type === 'tender' || l.type === 'TENDER' || l.type === 'PROPOSAL';

    if (lossViewType === 'prequalification') {
      baseLeads = inPeriod.filter(l => !isTenderItem(l));
      lost = baseLeads.filter((l) => l.stage === 'Disqualified');
      // "Won" prequalification means it advanced to at least Enlisted
      won = baseLeads.filter((l) => ['Enlisted', 'In Process', 'Won', 'Lost', 'Hold'].includes(l.stage));
    } else {
      // tender
      // Base leads are those that reached TP phase (either originated as prequal or tender)
      baseLeads = inPeriod.filter((l) => ['In Process', 'Won', 'Lost', 'Hold'].includes(l.stage) || isTenderItem(l));
      lost = baseLeads.filter((l) => l.stage === 'Lost');
      won = baseLeads.filter((l) => isWonStage(l.stage));
    }

    const counts = {};
    let unrecorded = 0;
    lost.forEach((l) => {
      const id = l.lostReason;
      if (!id || !LOSS_REASON_BY_ID[id]) { unrecorded += 1; return; }
      counts[id] = (counts[id] || 0) + 1;
    });

    const rows = LOSS_REASONS
      .map((r) => ({ ...r, count: counts[r.id] || 0 }))
      .filter((r) => r.count > 0)
      .sort((a, b) => b.count - a.count);

    const disqualified = DISQUALIFYING_REASONS.reduce((n, id) => n + (counts[id] || 0), 0);
    const realLosses = lost.length - disqualified - unrecorded;
    const closed = won.length + Math.max(0, realLosses);

    return {
      totalLost: lost.length,
      won: won.length,
      rows,
      unrecorded,
      disqualified,
      realLosses: Math.max(0, realLosses),
      rawRate: baseLeads.length ? (won.length / baseLeads.length) * 100 : 0,
      qualifiedRate: closed ? (won.length / closed) * 100 : 0,
      recorded: lost.length - unrecorded
    };
  }, [dashboardLeads, periodFrom, periodTo, lossViewType]);

  // ── CRM Summary computed data (date-filtered) ───────────────────────────
  const crmSummary = useMemo(() => {
    const today = new Date();
    const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
    const isValidDateStr = (d) => d && d !== '-';

    const fromTs = summaryFrom ? new Date(summaryFrom + 'T00:00:00').getTime() : 0;
    const toTs = summaryTo ? new Date(summaryTo + 'T23:59:59').getTime() : Infinity;

    const isInPeriod = (dateStr) => {
      if (!dateStr) return false;
      const t = new Date(dateStr).getTime();
      return !isNaN(t) && t >= fromTs && t <= toTs;
    };

    // Bulk-imported history is excluded so the team's numbers reflect work they
    // actually did. Two things this used to get wrong:
    //   Â· it matched inside words, and "old" lives inside "told" and "sold" —
    //     a rep writing "told him we're booked" silently deleted that prequalification from
    //     every column of this table;
    //   Â· it searched free-text notes at all, which is a rep's scratchpad, not
    //     a data-provenance field.
    // Now: whole words, source name only.
    const IMPORTED_SOURCE = /(^|[^a-z])(imported?|csv|legacy|old\s*data)([^a-z]|$)/i;
    const isCsvOrImported = (l) => IMPORTED_SOURCE.test(String(l.source || ''));

    const isCreatedInPeriod = (l) => !isCsvOrImported(l) && isInPeriod(l.inquiryDate || l.createdAt || l.created_at || l.date);
    const isLeadActive = (l) => {
      // Pending approval or rejected leads cannot be active
      if (l.approvalStatus === 'PENDING_APPROVAL' || l.approvalStatus === 'REJECTED') return false;
      return !isWonStage(l.stage) && l.stage !== 'Lost' && l.stage !== 'Disqualified';
    };

    const categorizeCalls = (prequalifications) => {
      const overdue = [], todayCalls = [], upcoming = [];
      prequalifications.forEach(prequalification => {
        // Pending approval or rejected leads cannot enter follow-up lists
        if (prequalification.approvalStatus === 'PENDING_APPROVAL' || prequalification.approvalStatus === 'REJECTED') return;

        const hasNextCall = !!prequalification.nextCallDate;
        const hasSiteVisit = isValidDateStr(prequalification.siteVisitDate);
        const targetDate = hasNextCall ? prequalification.nextCallDate : (hasSiteVisit ? prequalification.siteVisitDate : null);
        if (!targetDate) {
          if (Date.now() - new Date(prequalification.createdAt || 0).getTime() > 24 * 60 * 60 * 1000) overdue.push(prequalification);
          else todayCalls.push(prequalification);
          return;
        }
        const d = targetDate.split('T')[0];
        if (d < todayStr) overdue.push(prequalification);
        else if (d === todayStr) todayCalls.push(prequalification);
        else upcoming.push(prequalification);
      });
      return { overdue, today: todayCalls, upcoming };
    };

    const globalCallsLeads = [];
    const globalVisitsLeads = [];
    const globalTotalLeads = [];
    const globalActiveLeads = [];
    const leadsByStage = {};
    [...PREQUALIFICATION_STATUSES, ...AWARD_STATUSES].forEach(s => { leadsByStage[s] = []; });

    const salesMap = {};
    dashboardLeads.forEach(prequalification => {
      const name = prequalification.manager || 'Unassigned';
      if (!salesMap[name]) {
        salesMap[name] = {
          name, total: [], active: [], won: [], lost: [],
          preqTotal: [], preqActive: [],
          newLeads: [], submitted: [], pending: [], enlisted: [], disqualified: [],
          tpTotal: [], tpActive: [],
          inProcess: [], hold: [],
          callLeads: [], siteVisitLeads: [], revenue: 0
        };
      }

      const createdInPeriod = isCreatedInPeriod(prequalification);
      const wonInPeriod = createdInPeriod && isWonStage(prequalification.stage);
      const lostInPeriod = createdInPeriod && prequalification.stage === 'Lost';

      if (createdInPeriod) {
        salesMap[name].total.push(prequalification);
        globalTotalLeads.push(prequalification);

        const isPreq = !prequalification.type || prequalification.type === 'PREQUALIFICATION' || prequalification.uiType === 'Prequalification';
        const isTenderItem = prequalification.type === 'TENDER' || prequalification.type === 'PROPOSAL' || prequalification.type === 'tender' || prequalification.uiType === 'Tender';

        if (isPreq && ['New', 'Submitted', 'Pending', 'Enlisted', 'Disqualified'].includes(prequalification.stage)) {
          salesMap[name].preqTotal.push(prequalification);
        }
        if (isTenderItem && ['In Process', 'Won', 'Lost', 'Hold'].includes(prequalification.stage)) {
          salesMap[name].tpTotal.push(prequalification);
        }

        if (prequalification.stage === 'Disqualified') {
          salesMap[name].disqualified.push(prequalification);
          if (!leadsByStage[prequalification.stage]) leadsByStage[prequalification.stage] = [];
          leadsByStage[prequalification.stage].push(prequalification);
        }

        if (isLeadActive(prequalification)) {
          if (leadsByStage[prequalification.stage]) {
            leadsByStage[prequalification.stage].push(prequalification);
          }
          salesMap[name].active.push(prequalification);
          globalActiveLeads.push(prequalification);

          const isPreq = !prequalification.type || prequalification.type === 'PREQUALIFICATION' || prequalification.uiType === 'Prequalification';
          const isTenderItem = prequalification.type === 'TENDER' || prequalification.type === 'PROPOSAL' || prequalification.type === 'tender' || prequalification.uiType === 'Tender';

          if (isPreq && ['New', 'Submitted', 'Pending', 'Enlisted'].includes(prequalification.stage)) salesMap[name].preqActive.push(prequalification);
          if (isTenderItem && ['In Process', 'Hold'].includes(prequalification.stage)) salesMap[name].tpActive.push(prequalification);

          if (prequalification.stage === 'New') salesMap[name].newLeads.push(prequalification);
          else if (prequalification.stage === 'Submitted') salesMap[name].submitted.push(prequalification);
          else if (prequalification.stage === 'Pending') salesMap[name].pending.push(prequalification);
          else if (prequalification.stage === 'Enlisted') salesMap[name].enlisted.push(prequalification);
          else if (prequalification.stage === 'In Process') salesMap[name].inProcess.push(prequalification);
          else if (prequalification.stage === 'Hold') salesMap[name].hold.push(prequalification);
        }
      }

      if (wonInPeriod) {
        salesMap[name].won.push(prequalification);
        salesMap[name].revenue += safeAmount(prequalification.amount || prequalification.finalAmount || prequalification.quotationAmount);
        if (!leadsByStage[prequalification.stage]) leadsByStage[prequalification.stage] = [];
        leadsByStage[prequalification.stage].push(prequalification);
      }

      if (lostInPeriod) {
        salesMap[name].lost.push(prequalification);
        if (!leadsByStage[prequalification.stage]) leadsByStage[prequalification.stage] = [];
        leadsByStage[prequalification.stage].push(prequalification);
      }

      if (isLeadActive(prequalification)) {
        const isProfileItem = prequalification.type === 'profile' || prequalification.uiType === 'Profile' || prequalification.isProfile;
        const isCallLead =
          prequalification.approvalStatus !== 'PENDING_APPROVAL' &&
          prequalification.approvalStatus !== 'REJECTED' &&
          prequalification.stage !== 'Enlisted' &&
          (prequalification.type === 'TENDER' ||
            prequalification.type === 'PROPOSAL' ||
            isProfileItem ||
            prequalification.inCallList === true ||
            prequalification.stage === 'Call List' ||
            prequalification.stage === 'Site Visited' ||
            prequalification.stage === 'New' ||
            prequalification.stage === 'Submitted' ||
            prequalification.stage === 'Pending' ||
            !!prequalification.nextCallDate ||
            (isValidDateStr(prequalification.siteVisitDate) && !prequalification.siteVisited)) &&
          !(isProfileItem && (prequalification.stage === 'Interested' || prequalification.lastCallOutcome === 'connected_interested' || !!prequalification.prequalificationId)) &&
          !(prequalification.uiType === 'Prequalification' && !!prequalification.tenderId);

        if (isCallLead) {
          salesMap[name].callLeads.push(prequalification);
          globalCallsLeads.push(prequalification);
        }

        if (isValidDateStr(prequalification.siteVisitDate) && prequalification.stage !== 'Site Visited' && !prequalification.siteVisited) {
          salesMap[name].siteVisitLeads.push(prequalification);
          globalVisitsLeads.push(prequalification);
        }
      }
    });

    allContracts.forEach(contract => {
      let owner = 'Unassigned';
      if (contract.prequalificationId) {
        const preq = dashboardLeads.find(l => l.type !== 'tender' && l.id === contract.prequalificationId);
        if (preq) owner = preq.manager || 'Unassigned';
      } else if (contract.tenderId) {
        const tender = dashboardLeads.find(l => l.type === 'tender' && l.id === contract.tenderId);
        if (tender) owner = tender.manager || 'Unassigned';
      }

      if (!salesMap[owner]) {
        salesMap[owner] = {
          name: owner, total: [], active: [], won: [], lost: [],
          preqTotal: [], preqActive: [],
          newLeads: [], submitted: [], pending: [], enlisted: [], disqualified: [],
          tpTotal: [], tpActive: [],
          inProcess: [], hold: [],
          callLeads: [], siteVisitLeads: [], revenue: 0,
          contractsExpiring: [], contractsExpired: [], contractsExtended: []
        };
      } else {
        if (!salesMap[owner].contractsExpiring) salesMap[owner].contractsExpiring = [];
        if (!salesMap[owner].contractsExpired) salesMap[owner].contractsExpired = [];
        if (!salesMap[owner].contractsExtended) salesMap[owner].contractsExtended = [];
      }

      const today = new Date();
      today.setHours(0,0,0,0);
      const next30 = new Date(today);
      next30.setDate(today.getDate() + 30);
      next30.setHours(23,59,59,999);

      if (contract.status === 'Active' && contract.endDate) {
        const end = new Date(contract.endDate);
        if (end >= today && end <= next30) {
          salesMap[owner].contractsExpiring.push(contract);
        }
      }
      if (contract.status === 'Expired') {
        salesMap[owner].contractsExpired.push(contract);
      }
      if (contract.activities && contract.activities.length > 0) {
        salesMap[owner].contractsExtended.push(contract);
      }
    });

    const globalCalls = categorizeCalls(globalCallsLeads);

    const salespeople = Object.values(salesMap)
      .filter(sp => sp.total.length > 0 || sp.won.length > 0 || sp.lost.length > 0 || sp.callLeads.length > 0 || sp.siteVisitLeads.length > 0 || (sp.contractsExpiring && sp.contractsExpiring.length > 0) || (sp.contractsExpired && sp.contractsExpired.length > 0) || (sp.contractsExtended && sp.contractsExtended.length > 0))
      .map(sp => ({
        ...sp,
        contractsExpiring: sp.contractsExpiring || [],
        contractsExpired: sp.contractsExpired || [],
        contractsExtended: sp.contractsExtended || [],
        convRate: sp.total.length > 0 ? Math.round((sp.won.length / sp.total.length) * 100) : 0,
        preqConvRate: sp.preqTotal.length > 0 ? Math.round((sp.enlisted.length / sp.preqTotal.length) * 100) : 0,
        tpConvRate: sp.tpTotal.length > 0 ? Math.round((sp.won.length / sp.tpTotal.length) * 100) : 0,
        calls: categorizeCalls(sp.callLeads),
        visits: {
          all: sp.siteVisitLeads,
          overdue: sp.siteVisitLeads.filter(l => (l.siteVisitDate || '').split('T')[0] < todayStr),
          today: sp.siteVisitLeads.filter(l => (l.siteVisitDate || '').split('T')[0] === todayStr),
          upcoming: sp.siteVisitLeads.filter(l => (l.siteVisitDate || '').split('T')[0] > todayStr),
        },
      }))
      .sort((a, b) => b.total.length - a.total.length || b.revenue - a.revenue);

    return {
      totalLeads: globalTotalLeads.length,
      allLeads: globalTotalLeads,
      activeLeads: globalActiveLeads,
      leadsByStage,
      callLeads: globalCallsLeads,
      overdueCallLeads: globalCalls.overdue,
      todayCallLeads: globalCalls.today,
      upcomingCallLeads: globalCalls.upcoming,
      siteVisitLeads: globalVisitsLeads,
      overdueVisits: globalVisitsLeads.filter(l => (l.siteVisitDate || '').split('T')[0] < todayStr),
      todayVisits: globalVisitsLeads.filter(l => (l.siteVisitDate || '').split('T')[0] === todayStr),
      upcomingVisits: globalVisitsLeads.filter(l => (l.siteVisitDate || '').split('T')[0] > todayStr),
      salespeople,
    };
  }, [dashboardLeads, summaryFrom, summaryTo, allContracts]);


  // ── Summary modal prequalifications list ─────────────────────────────────────────────
  const getSummaryModalLeads = () => {
    if (!summaryModalType) return [];
    // Salesperson-scoped modal: 'sp:Name:type'
    if (summaryModalType.startsWith('sp:')) {
      const parts = summaryModalType.split(':');
      const spName = parts[1];
      const type = parts[2];
      const sp = crmSummary.salespeople.find(s => s.name === spName);
      if (!sp) return [];
      switch (type) {
        case 'total': return sp.total;
        case 'active': return sp.active;
        case 'preqTotal': return sp.preqTotal;
        case 'preqActive': return sp.preqActive;
        case 'new': return sp.newLeads;
        case 'submitted': return sp.submitted;
        case 'pending': return sp.pending;
        case 'enlisted': return sp.enlisted;
        case 'disqualified': return sp.disqualified;
        case 'tpTotal': return sp.tpTotal;
        case 'tpActive': return sp.tpActive;
        case 'inProcess': return sp.inProcess;
        case 'hold': return sp.hold;
        case 'won': return sp.won;
        case 'lost': return sp.lost;
        case 'calls-all': return sp.callLeads;
        case 'calls-overdue': return sp.calls.overdue;
        case 'calls-today': return sp.calls.today;
        case 'calls-upcoming': return sp.calls.upcoming;
        case 'visits-all': return sp.visits.all;
        case 'visits-overdue': return sp.visits.overdue;
        case 'visits-today': return sp.visits.today;
        case 'visits-upcoming': return sp.visits.upcoming;
        default: return [];
      }
    }
    if (summaryModalType.startsWith('stage-')) {
      const stage = summaryModalType.replace('stage-', '');
      return crmSummary.leadsByStage[stage] || [];
    }
    switch (summaryModalType) {
      case 'prequalifications-all': return crmSummary.allLeads;
      case 'prequalifications-active': return crmSummary.activeLeads;
      case 'preq-total': return crmSummary.allLeads.filter(l => ['New', 'Submitted', 'Pending', 'Enlisted', 'Disqualified'].includes(l.stage));
      case 'tp-total': return crmSummary.allLeads.filter(l => ['In Process', 'Won', 'Lost', 'Hold'].includes(l.stage));
      case 'preq-active': return crmSummary.activeLeads.filter(l => ['New', 'Submitted', 'Pending', 'Enlisted'].includes(l.stage));
      case 'tp-active': return crmSummary.activeLeads.filter(l => ['In Process', 'Hold'].includes(l.stage));
      case 'calls-all': return crmSummary.callLeads;
      case 'calls-overdue': return crmSummary.overdueCallLeads;
      case 'calls-today': return crmSummary.todayCallLeads;
      case 'calls-upcoming': return crmSummary.upcomingCallLeads;
      case 'visits-all': return crmSummary.siteVisitLeads;
      case 'visits-overdue': return crmSummary.overdueVisits;
      case 'visits-today': return crmSummary.todayVisits;
      case 'visits-upcoming': return crmSummary.upcomingVisits;
      default: return [];
    }
  };

  const getSummaryModalTitle = () => {
    if (!summaryModalType) return '';
    if (summaryModalType.startsWith('sp:')) {
      const parts = summaryModalType.split(':');
      const spName = parts[1];
      const type = parts[2];
      const prequalifications = getSummaryModalLeads();
      const typeLabel = {
        'total': 'All Prequalifications', 'active': 'Active', 'won': 'Won', 'lost': 'Lost', 'disqualified': 'Disqualified',
        'calls-all': 'Calls', 'calls-overdue': 'Overdue Calls', 'calls-today': "Today's Calls", 'calls-upcoming': 'Upcoming Calls',
        'visits-all': 'Site Visits', 'visits-overdue': 'Overdue Visits', 'visits-today': "Today's Visits", 'visits-upcoming': 'Upcoming Visits',
      }[type] || type;
      return `${spName} — ${typeLabel} (${prequalifications.length})`;
    }
    if (summaryModalType.startsWith('stage-')) {
      const stage = summaryModalType.replace('stage-', '');
      const count = (crmSummary.leadsByStage[stage] || []).length;
      return `Stage: ${stage} (${count} prequalifications)`;
    }
    const labels = {
      'prequalifications-all': `All Prequalifications (${crmSummary.allLeads.length})`,
      'prequalifications-active': `Active Prequalifications (${crmSummary.activeLeads.length})`,
      'preq-total': `Total Prequalifications (${crmSummary.allLeads.filter(l => ['New', 'Submitted', 'Pending', 'Enlisted', 'Disqualified'].includes(l.stage)).length})`,
      'tp-total': `Total Tenders/Proposals (${crmSummary.allLeads.filter(l => ['In Process', 'Won', 'Lost', 'Hold'].includes(l.stage)).length})`,
      'preq-active': `Active Prequalifications (${crmSummary.activeLeads.filter(l => ['New', 'Submitted', 'Pending', 'Enlisted'].includes(l.stage)).length})`,
      'tp-active': `Active Tenders/Proposals (${crmSummary.activeLeads.filter(l => ['In Process', 'Hold'].includes(l.stage)).length})`,
      'calls-all': `All Calls (${crmSummary.callLeads.length})`,
      'calls-overdue': `Overdue Calls (${crmSummary.overdueCallLeads.length})`,
      'calls-today': `Today's Calls (${crmSummary.todayCallLeads.length})`,
      'calls-upcoming': `Upcoming Calls (${crmSummary.upcomingCallLeads.length})`,
      'visits-all': `All Site Visits (${crmSummary.siteVisitLeads.length})`,
      'visits-overdue': `Overdue Site Visits (${crmSummary.overdueVisits.length})`,
      'visits-today': `Today's Site Visits (${crmSummary.todayVisits.length})`,
      'visits-upcoming': `Upcoming Site Visits (${crmSummary.upcomingVisits.length})`,
    };
    return labels[summaryModalType] || '';
  };

  // Get filtered prequalifications for drilldown
  const getFilteredLeads = () => {
    switch (activeModal) {
      case 'prequalifications':
        return metrics.drilldownLeads;
      case 'active':
        // Same rule as the card and as the server: still winnable.
        return (metrics.drilldownLeads || []).filter((l) => !isWonStage(l.stage) && l.stage !== 'Lost' && l.stage !== 'Disqualified');
      case 'enlisted':
        return metrics.enlistedDrilldownLeads || ((metrics.drilldownLeads || []).filter((l) => l.stage === 'Enlisted'));
      case 'tenders':
        return metrics.tenderDrilldownLeads || [];
      case 'active-tenders':
        return (metrics.tenderDrilldownLeads || []).filter(t => t.stage !== 'Won' && t.stage !== 'Lost');
      case 'won-tenders':
        return (metrics.tenderDrilldownLeads || []).filter(t => t.stage === 'Won');
      case 'booked':
        // C-02: use the server-side bookedAt-filtered list, NOT a client-side stage filter on createdAt data
        return metrics.bookedDrilldownLeads;
      case 'conversion':
        // The conversion card counts this period's own prequalifications that have booked,
        // so its list must be those prequalifications — not the bookedAt-filtered list,
        // which is a different set of people (that one belongs to 'booked').
        return (metrics.drilldownLeads || []).filter((l) => isWonStage(l.stage));
      case 'stale':
        return metrics.staleLeads;
      case 'pipeline':
        return pipelineLeads;
      case 'tentative':
        return tentativeLeads;
      case 'ytd':
        return metrics.ytdDrilldownLeads;
      case 'source':
        if (!selectedSource) return [];
        const normSource = normalizeSource(selectedSource);
        let result = [];
        if (typeFilter === 'All' || typeFilter === 'Prequalification') {
          result = [...result, ...(metrics.drilldownLeads || []).filter((l) => normalizeSource(l.source) === normSource)];
        }
        if (typeFilter === 'All' || typeFilter === 'Tender/Proposal') {
          const tendersResult = filteredTenders
            .filter(t => normalizeSource(typeof t.source === 'object' ? t.source?.name : t.source) === normSource)
            .map(t => ({
              id: t.id,
              clientName: t.clientName || 'Unknown',
              phone: t.contactPersonPhone || '',
              stage: t.status,
              manager: t.assignee?.username || 'Unassigned',
              source: typeof t.source === 'object' ? t.source?.name : t.source || '',
              amount: t.quotationAmount || t.estimatedValue || 0,
              inquiryDate: typeof t.createdAt === 'string' ? t.createdAt.split('T')[0] : 'Unknown',
              type: 'tender'
            }));
          result = [...result, ...tendersResult];
        }
        return result;
      case 'funnel':
        return selectedStage
          ? metrics.drilldownLeads.filter((l) => {
            const cap = (s) => s ? s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : 'New';
            const stageMapping = { 'New Prequalification': 'New', 'Proposal': 'Quoted', 'Booked': 'Won', 'Negotiation': 'Negotiation' };
            const normalized = stageMapping[cap(l.stage)] || cap(l.stage);
            return normalized === selectedStage;
          })
          : [];
      default:
        return [];
    }
  };

  const getModalTitle = () => {
    switch (activeModal) {
      case 'prequalifications': return `${t('totalLeads')} (${metrics.periodLeads})`;
      case 'active': return `${t('activeLeads')} (${metrics.activePeriodLeads})`;
      case 'enlisted': return `${t('enlistedLeads') || 'Enlisted Prequalifications'} (${metrics.enlistedPeriodLeads})`;
      case 'tenders': return `${t('totalTenders') || 'Total Tenders/Proposals'} (${metrics.totalTenders})`;
      case 'active-tenders': return `${t('activeTenders') || 'Active Tenders/Proposals'} (${metrics.activeTenders})`;
      case 'won-tenders': return `${t('wonTenders') || 'Award Won Tenders/Proposals'} (${metrics.wonTenders})`;
      case 'booked': return `Booked This Period (${metrics.bookedPeriodLeads})`;
      case 'conversion': return `${t('bookedFromThisPeriod') || 'Booked from this period\'s prequalifications'} (${metrics.cohortBookedLeads})`;
      case 'stale': return `${t('staleLeads')} (${metrics.staleLeads.length})`;
      case 'pipeline': return `${t('pipelineValue')} — ${formatCurrency(analyticsData?.pipelinePotential || 0)} (${pipelineLeads.length})`;
      case 'tentative': return `${t('tentativeBookings')} (${tentativeLeads.length})`;
      case 'ytd': return `${t('revenueYtd')} - ${formatCurrency(metrics.ytdRevenue)}`;
      case 'source': return `${t('source')}: ${selectedSource}`;
      case 'funnel': return `${t('stage')}: ${selectedStage}`;
      default: return '';
    }
  };

  // ── Shared prequalification row renderer for all modals ──────────────────────────────
  // showBookedOn: only the analytics drill-down lists carry bookedAt/inquiryDate
  // from the server — SummaryModal prequalifications don't, so it stays off there.
  const LeadTableRow = ({ prequalification, index = 0, isBookedList = false, showBookedOn = false }) => (
    <tr
      key={prequalification.id}
      className="hover:bg-blue-50/50 cursor-pointer transition-colors"
      onClick={() => navigate(`?${prequalification.type === 'tender' ? 'tenderId' : (prequalification.uiType === 'Profile' || prequalification.type === 'profile') ? 'profileId' : 'prequalificationId'}=${prequalification.id}`)}
    >
      <td className="px-4 py-3 max-w-[200px]">
        <div className="font-semibold text-gray-900 truncate">{prequalification.clientName}</div>
        <div className="text-xs text-gray-400 truncate">{prequalification.phone}</div>
      </td>
      {showBookedOn && (
        <td className="px-4 py-3 text-xs whitespace-nowrap">
          {prequalification.bookedAt ? (
            <span className="text-gray-700 font-medium">
              {formatDate(prequalification.bookedAt, { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          ) : <span className="text-gray-300">—</span>}
        </td>
      )}
      {!isBookedList && (
        <>
          <td className="px-4 py-3 whitespace-nowrap">
            <span className={`px-2 py-1 rounded-lg text-xs font-semibold whitespace-nowrap ${STAGE_COLORS[prequalification.stage]?.bg || 'bg-gray-100'} ${STAGE_COLORS[prequalification.stage]?.text || 'text-gray-600'}`}>
              {prequalification.stage}
            </span>
          </td>
          <td className="px-4 py-3 hidden sm:table-cell whitespace-nowrap">
            <span className="text-xs text-gray-500">{prequalification.manager || '—'}</span>
          </td>
          <td className="px-4 py-3 hidden sm:table-cell whitespace-nowrap">
            {prequalification.source ? (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">{typeof prequalification.source === 'object' ? prequalification.source?.name : prequalification.source}</span>
            ) : <span className="text-gray-300">—</span>}
          </td>
          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
            {/* Analytics drill-downs carry inquiryDate; SummaryModal prequalifications carry
                the raw createdAt. Reading only one left the column blank in
                every drill-down opened from a KPI card. */}
            {formatDate(prequalification.createdAt || prequalification.inquiryDate, { day: 'numeric', month: 'short', year: 'numeric' })}
          </td>
        </>
      )}

      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
        {prequalification.eventDate ? formatDate(prequalification.eventDate, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
      </td>

      {isBookedList ? (
        <>
          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
            {prequalification.venue || '—'}
          </td>
          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
            {prequalification.shift || '—'}
          </td>
          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
            {prequalification.manager || '—'}
          </td>
        </>
      ) : (
        <>
          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
            {prequalification.clientBudget ? formatCurrency(prequalification.clientBudget) : '—'}
          </td>
          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap font-medium">
            {prequalification.amount || prequalification.quotationAmount ? formatCurrency(prequalification.amount || prequalification.quotationAmount) : '—'}
          </td>
        </>
      )}

      <td className="px-4 py-3 text-center">
        <a
          href={getWhatsappLink(prequalification.phone)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg inline-block"
        >
          <MessageCircle className="w-4 h-4" />
        </a>
      </td>
    </tr>
  );

  // ── Summary Modal (CRM card drill-down) ──────────────────────────────────
  const SummaryModal = () => {
    if (!summaryModalType) return null;
    const prequalifications = getSummaryModalLeads();
    const visible = prequalifications.slice(0, visibleCount);
    const hasMore = visibleCount < prequalifications.length;
    const isBookedList = summaryModalType.toLowerCase().includes('won');

    return (
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onMouseDown={e => { if (e.target === e.currentTarget) { setSummaryModalType(null); setVisibleCount(50); } }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", duration: 0.5, bounce: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] max-h-[80vh] overflow-hidden flex flex-col"
        >
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center flex-shrink-0">
            <h3 className="font-bold text-gray-900 text-base">{getSummaryModalTitle()}</h3>
            <button
              onClick={() => { setSummaryModalType(null); setVisibleCount(50); }}
              className="p-2 hover:bg-gray-200 rounded-lg"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="overflow-y-auto flex-1">
            {prequalifications.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No prequalifications found</p>
              </div>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr className="text-left text-gray-500 whitespace-nowrap">
                      <th className="px-4 py-3">{t('client')}</th>
                      {!isBookedList && (
                        <>
                          <th className="px-4 py-3">{t('stage')}</th>
                          <th className="px-4 py-3 hidden sm:table-cell">{t('assigned')}</th>
                          <th className="px-4 py-3 hidden sm:table-cell">{t('source')}</th>
                          <th className="px-4 py-3">{t('createdDate')}</th>
                        </>
                      )}
                      <th className="px-4 py-3">{t('eventDate')}</th>
                      {isBookedList ? (
                        <>
                          <th className="px-4 py-3">Venue</th>
                          <th className="px-4 py-3">Shift</th>
                          <th className="px-4 py-3">{t('assigned')}</th>
                        </>
                      ) : (
                        <>
                          <th className="px-4 py-3">{t('clientBudget')}</th>
                          <th className="px-4 py-3">{t('quotation')}</th>
                        </>
                      )}
                      <th className="px-4 py-3 text-center">{t('wa')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visible.map((prequalification, i) => <LeadTableRow key={prequalification.id} prequalification={prequalification} index={i} isBookedList={isBookedList} />)}
                  </tbody>
                </table>
                {hasMore && (
                  <div className="flex justify-center py-5">
                    <button
                      onClick={() => setVisibleCount(c => c + 50)}
                      className="px-4 py-1.5 bg-gray-100 text-gray-600 hover:bg-gray-200 text-sm font-medium rounded-lg transition-colors"
                    >
                      {t('loadMoreRemaining').replace('{remaining}', prequalifications.length - visibleCount)}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="p-3 border-t bg-gray-50 text-xs text-gray-500 flex-shrink-0 flex items-center justify-between">
            <span>{t('showingXofYleads').replace('{x}', visible.length).replace('{y}', prequalifications.length)}</span>
          </div>
        </motion.div>
      </div>
    );
  };

  // Drilldown Modal Component
  const DrilldownModal = () => {
    if (!activeModal) return null;
    const filteredLeads = getFilteredLeads();
    const visibleLeads = filteredLeads.slice(0, visibleCount);
    const hasMore = visibleCount < filteredLeads.length;
    const isBookedList = activeModal === 'booked' || activeModal === 'conversion';

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) { setActiveModal(null); setSelectedSource(null); setSelectedStage(null); setVisibleCount(50); } }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", duration: 0.5, bounce: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] max-h-[80vh] overflow-hidden flex flex-col"
        >
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center flex-shrink-0">
            <h3 className="font-bold text-gray-900">{getModalTitle()}</h3>
            <button
              onClick={() => { setActiveModal(null); setSelectedSource(null); setSelectedStage(null); setVisibleCount(50); }}
              className="p-2 hover:bg-gray-200 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="overflow-y-auto flex-1">
            {filteredLeads.length === 0 ? (
              <div className="p-8 text-center text-gray-400">{t('noBookingsFound').replace('bookings', 'prequalifications')}</div>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr className="text-left text-gray-500 whitespace-nowrap">
                      <th className="px-4 py-3">{t('client')}</th>
                      {isBookedList && (
                        <th className="px-4 py-3">{t('bookedOn') || 'Booked On'}</th>
                      )}
                      {!isBookedList && (
                        <>
                          <th className="px-4 py-3">{t('stage')}</th>
                          <th className="px-4 py-3 hidden sm:table-cell">{t('assigned')}</th>
                          <th className="px-4 py-3 hidden sm:table-cell">{t('source')}</th>
                          <th className="px-4 py-3">{t('createdDate')}</th>
                        </>
                      )}
                      <th className="px-4 py-3">{t('eventDate')}</th>
                      {isBookedList ? (
                        <>
                          <th className="px-4 py-3">Venue</th>
                          <th className="px-4 py-3">Shift</th>
                          <th className="px-4 py-3">{t('assigned')}</th>
                        </>
                      ) : (
                        <>
                          <th className="px-4 py-3">{t('clientBudget')}</th>
                          <th className="px-4 py-3">{t('quotation')}</th>
                        </>
                      )}
                      <th className="px-4 py-3 text-center">{t('wa')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {visibleLeads.map((prequalification, i) => (
                      <LeadTableRow key={prequalification.id} prequalification={prequalification} index={i} isBookedList={isBookedList} showBookedOn={isBookedList} />
                    ))}
                  </tbody>
                </table>
                {hasMore && (
                  <div className="flex justify-center py-5">
                    <button
                      onClick={() => setVisibleCount((c) => c + 50)}
                      className="px-6 py-2 border border-blue-500 text-blue-600 hover:bg-blue-50 text-sm font-medium rounded-lg transition-colors"
                    >
                      {t('loadMoreRemaining').replace('{remaining}', filteredLeads.length - visibleCount)}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="p-3 md:p-4 border-t bg-gray-50 text-xs text-gray-500 flex-shrink-0 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div>
              {`Showing ${visibleLeads.length} of ${filteredLeads.length} ${typeFilter === 'All' ? (t('records') || 'records').toLowerCase() : (typeFilter === 'Prequalification' ? t('prequalifications').toLowerCase() : t('tendersProposals').toLowerCase())}`}
            </div>

          </div>
        </motion.div>
      </div>
    );
  };

  const followUpStats = useMemo(() => {
    let overdue = 0;
    let today = 0;
    let upcoming = 0;

    const todayDateStr =
      new Date().getFullYear() +
      '-' + String(new Date().getMonth() + 1).padStart(2, '0') +
      '-' + String(new Date().getDate()).padStart(2, '0');
    const nowMs = Date.now();

    const all = [
      ...(prequalifications || []).map(p => ({ ...p, uiType: 'Prequalification' })),
      ...(tenders || []).map(t => ({ ...t, uiType: 'Tender', isTender: true, stage: t.status, nextCallDate: t.nextCallDate || (t.followUpDate ? t.followUpDate.split('T')[0] : null) })),
      ...(profiles || []).map(p => ({ ...p, uiType: 'Profile', isProfile: true, stage: p.status || 'New' }))
    ];

    const activeLeads = all.filter(
      l => {
        // Approval check: Only approved items (or legacy items without approvalStatus) enter the follow-up list
        if (l.approvalStatus === 'PENDING_APPROVAL' || l.approvalStatus === 'REJECTED') {
          return false;
        }

        return (
          l.stage !== 'Enlisted' && l.stage !== 'Disqualified' &&
          (l.isTender || l.isProfile || l.inCallList === true || l.stage === 'Call List' || l.stage === 'Site Visited' || l.stage === 'New' || l.stage === 'Submitted' || l.stage === 'Pending' || !!l.nextCallDate || (l.siteVisitDate && l.siteVisitDate !== '-' && !l.siteVisited)) &&
          !isWonStage(l.stage) &&
          l.stage !== 'Lost' &&
          !(l.isProfile && (l.stage === 'Interested' || l.lastCallOutcome === 'connected_interested' || !!l.prequalificationId)) &&
          !(l.uiType === 'Prequalification' && !!l.tenderId)
        );
      }
    );

    activeLeads.forEach(l => {
      const targetDate = l.nextCallDate || (l.siteVisitDate && l.siteVisitDate !== '-' ? l.siteVisitDate : null);
      if (!targetDate) {
        if (nowMs - new Date(l.createdAt || 0).getTime() > 24 * 60 * 60 * 1000) overdue++;
        else today++;
        return;
      }
      const nextDateStr = targetDate.split('T')[0];
      if (nextDateStr < todayDateStr) overdue++;
      else if (nextDateStr === todayDateStr) {
          if (l.nextCallTime) {
            const scheduled = new Date(`${nextDateStr}T${l.nextCallTime}:00`);
            if (!isNaN(scheduled.getTime()) && nowMs - scheduled.getTime() > 24 * 60 * 60 * 1000) overdue++;
            else today++;
          } else {
             today++;
          }
      }
      else upcoming++;
    });

    return { overdue, today, upcoming };
  }, [prequalifications, tenders, profiles]);

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Drilldown Modal */}
      {DrilldownModal()}
      {/* CRM Summary Modal */}
      {SummaryModal()}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
            {t('ownerDashboard')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('welcomeBack')}, <span className="font-semibold text-gray-900">{/* currentUser?.name || */ 'Admin'}</span>
          </p>
        </div>

        {/* Quick Actions — rolling windows (see dateRange comment above).
            `items-stretch` below md is load-bearing, not cosmetic: with
            `items-end`, the 491px-wide button strip refused to shrink to its
            345px container and the surplus hung off the LEFT edge of the
            screen — its own overflow-x never engaged (scrollWidth ===
            clientWidth), the page doesn't scroll sideways, and so "Last 30
            days" sat at -129px, permanently unclickable. Since 30 days is the
            default, tapping any other range on a phone was a one-way door. */}
        <div className="flex flex-col items-stretch md:items-end gap-2 w-full md:w-auto">
          {/* Phones get a dropdown. Six buttons cannot fit a 375px screen at
              any font size worth reading, and a horizontal scroller with a
              hidden scrollbar gives no hint the other options exist. */}
          <Dropdown className="w-full md:hidden">
            <DropdownTrigger>
              <button className="w-full bg-white hover:bg-slate-50 border border-gray-200 rounded-xl text-sm px-3 h-[38px] shadow-sm flex justify-between items-center text-left transition-colors">
                <span className="flex items-center gap-2 font-semibold text-gray-700 truncate">
                  <Calendar className="w-4 h-4 text-blue-500 shrink-0" />
                  {(DATE_RANGES.find(([k]) => k === dateRange) || [])[1]}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
              </button>
            </DropdownTrigger>
            <DropdownPopover>
              <DropdownMenu
                className="!max-h-none"
                selectedKeys={new Set([dateRange])}
                selectionMode="single"
                onSelectionChange={(keys) => setDateRange(Array.from(keys)[0])}
              >
                {DATE_RANGES.map(([key, label]) => (
                  <DropdownItem key={key} id={key}>{label}</DropdownItem>
                ))}
              </DropdownMenu>
            </DropdownPopover>
          </Dropdown>

          <div className="hidden md:flex bg-white rounded-xl shadow-sm border p-1 whitespace-nowrap">
            {DATE_RANGES.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setDateRange(key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dateRange === key ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
                  }`}
              >
                {label}
              </button>
            ))}
          </div>
          {/* The pickers flex on a phone rather than holding a fixed 128px
              each — two fixed columns plus the icon and arrow is the same
              overflow trap the strip above just escaped. */}
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-1.5 shadow-sm w-full md:w-auto">
              <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
              <div className="flex-1 min-w-0 md:flex-none md:w-32">
                <DatePicker
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="border-0 shadow-none bg-transparent !p-0 min-h-0 !h-auto text-xs sm:text-sm font-medium"
                  fullWidth
                />
              </div>
              <span className="text-gray-300 text-xs shrink-0">→</span>
              <div className="flex-1 min-w-0 md:flex-none md:w-32">
                <DatePicker
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="border-0 shadow-none bg-transparent !p-0 min-h-0 !h-auto text-xs sm:text-sm font-medium"
                  fullWidth
                />
              </div>
            </div>
          )}
        </div>
      </div>





      {/* Stats Grid */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          {t('quickStats')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">

          {/* ── Pending Approvals Quick Stats (CEO & GM Operations) ── */}
          {isApproverUser && (
            <>
              {/* Profiles Pending */}
              <div
                onClick={() => navigate('/approvals?type=PROFILE&status=PENDING')}
                className="bg-white p-4 rounded-2xl border shadow-sm transition-all group relative overflow-hidden cursor-pointer hover:shadow-md hover:border-blue-200"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="p-2 bg-blue-100 text-blue-600 rounded-xl group-hover:scale-110 transition-transform">
                    <Contact className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList') || 'View List'}</span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium truncate" title={t('profilesPendingApproval') || 'Profiles Pending Approval'}>
                    {t('profilesPending') || 'Profiles Pending'}
                  </p>
                  <StatValue loading={approvalCountsLoading}>{approvalCounts.pendingProfiles || 0}</StatValue>
                </div>
              </div>

              {/* Prequalifications Pending */}
              <div
                onClick={() => navigate('/approvals?type=PREQUALIFICATION&status=PENDING')}
                className="bg-white p-4 rounded-2xl border shadow-sm transition-all group relative overflow-hidden cursor-pointer hover:shadow-md hover:border-amber-200"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="p-2 bg-amber-100 text-amber-600 rounded-xl group-hover:scale-110 transition-transform">
                    <ClipboardCheck className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList') || 'View List'}</span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium truncate" title={t('prequalificationsPendingApproval') || 'Prequalifications Pending Approval'}>
                    {t('prequalificationsPending') || 'Prequalifications Pending'}
                  </p>
                  <StatValue loading={approvalCountsLoading}>{approvalCounts.pendingPrequalifications || 0}</StatValue>
                </div>
              </div>

              {/* Tenders / Proposals Pending */}
              <div
                onClick={() => navigate('/approvals?type=TENDER&status=PENDING')}
                className="bg-white p-4 rounded-2xl border shadow-sm transition-all group relative overflow-hidden cursor-pointer hover:shadow-md hover:border-purple-200"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="p-2 bg-purple-100 text-purple-600 rounded-xl group-hover:scale-110 transition-transform">
                    <FileText className="w-5 h-5" />
                  </div>
                  <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList') || 'View List'}</span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium truncate" title={t('tendersPendingApproval') || 'Tenders / Proposals Pending Approval'}>
                    {t('tendersPending') || 'Tenders / Proposals Pending'}
                  </p>
                  <StatValue loading={approvalCountsLoading}>{approvalCounts.pendingTenders || 0}</StatValue>
                </div>
              </div>
            </>
          )}

          {/* Proposal Value (CRM) */}
          <div
            onClick={() => setActiveModal('pipeline')}
            className="bg-white p-4 rounded-2xl border shadow-sm transition-all group relative overflow-hidden cursor-pointer hover:shadow-md hover:border-indigo-200"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl group-hover:scale-110 transition-transform">
                <TrendingUp className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList')}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('pipelineScopeNote')}>{t('pipelineValue') || 'Proposal Value (CRM)'}</p>
              <StatValue loading={kpiLoading}>{formatCurrency(analyticsData?.pipelinePotential || 0)}</StatValue>
              {/* Two disclosures the old single number hid: this is every live
                  deal (not just ones from the selected window), and it counts
                  our quotes only — prequalifications carrying just a stated budget are
                  potential, not pipeline, so they're named, not added in. */}
              {!kpiLoading && analyticsData?.pipelineQuotedCount !== undefined && (
                <p className="text-[10px] text-gray-400 mt-1 leading-tight">
                  {analyticsData.pipelineQuotedCount} {t('quotedOpenDeals')}
                  {analyticsData?.pipelineBudgetOnlyCount > 0 && (
                    <> &middot; {String(t('budgetOnlyLeads')).replace('{n}', analyticsData.pipelineBudgetOnlyCount)}</>
                  )}
                </p>
              )}
            </div>
          </div>



          {/* Revenue Card Removed (C-01) */}

          {/* Booked This Period — the HEADLINE count, read straight from the
              banquet system (C-02/D-01: booking figures come from the BMS).
              Complete: includes walk-ins and direct BMS bookings that never
              passed through the CRM, which prequalification-side counting can never cover.
              View List = the same bookings in Finance, period applied. The
              CRM-prequalification subset (attribution) lives in the Sales Team Summary.
              Sits outside the fence, so it ignores the axis toggle and always
              opens the booking-date list its own number is counted on. */}
          <div
            onClick={() => openInFinance('all', 'booking')}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-blue-200 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">View List</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('contractsCreated') || 'Contracts Created'}>{t('contractsCreated') || 'Contracts Created'}</p>
              <StatValue loading={finLoading}>{banquetFinancials.count}</StatValue>
            </div>
          </div>

          {/* Expiring Contracts — contracts expiring within 30 days */}
          <div
            onClick={() => navigate('/contracts?urgency=warning&status=Active')}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-red-200 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-red-100 text-red-600 rounded-xl">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList') || 'View List'}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('expiringWithin45Days') || 'Expiring in 45 Days'}>{t('expiringWithin45Days') || 'Expiring in 45 Days'}</p>
              <StatValue loading={contractsLoading}>{expiringContractsCount}</StatValue>
            </div>
          </div>

          {/* Expired Contracts */}
          <div
            onClick={() => navigate('/contracts?status=Expired')}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-red-300 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-red-100 text-red-700 rounded-xl">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList') || 'View List'}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('expiredContracts') || 'Expired Contracts'}>{t('expiredContracts') || 'Expired Contracts'}</p>
              <StatValue loading={contractsLoading}>{expiredContractsCount}</StatValue>
            </div>
          </div>

          {/* Total Clients */}
          <div
            onClick={() => navigate('/profiles')}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-indigo-200 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl">
                <User className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList') || 'View List'}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('totalClients') || 'Total Clients'}>{t('totalClients') || 'No. of Clients'}</p>
              <StatValue loading={profilesLoading}>{(profilesPagination?.total ?? profiles.length).toLocaleString()}</StatValue>
            </div>
          </div>

          {/* Total Prequalifications — every inquiry that arrived in the period, before any
              filtering. Sits next to Active on purpose: the pair reads "this
              many came in, this many are still winnable". */}
          <div
            onClick={() => setActiveModal('prequalifications')}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-blue-200 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-sky-100 text-sky-600 rounded-xl">
                <Users className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList')}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('totalLeadsPeriodNote')}>{t('totalLeads')}</p>
              <StatValue loading={kpiLoading}>{(metrics.totalLeadsAll || metrics.totalLeads || metrics.periodLeads).toLocaleString()}</StatValue>
            </div>
          </div>

          {/* Active Prequalifications — not won, NOT LOST. The old figure subtracted only
              bookings, so with 96% of the database Lost it was reporting the
              whole intake as work in progress. */}
          <div
            onClick={() => setActiveModal('active')}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-amber-200 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-amber-100 text-amber-600 rounded-xl">
                <Users className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList')}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('activeLeadsNote')}>{t('activeLeads')}</p>
              <StatValue loading={kpiLoading}>{metrics.activePeriodLeads.toLocaleString()}</StatValue>
            </div>
          </div>

          {/* Enlisted Prequalifications */}
          <div
            onClick={() => setActiveModal('enlisted')}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-teal-200 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-teal-100 text-teal-600 rounded-xl">
                <Users className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList')}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('enlistedLeadsNote') || 'Enlisted Prequalifications'}>{t('enlistedLeads') || 'Enlisted Prequalifications'}</p>
              <StatValue loading={kpiLoading}>{(metrics.enlistedLeadsAll || metrics.enlistedPeriodLeads || 0).toLocaleString()}</StatValue>
            </div>
          </div>

          {/* Total Tenders */}
          <div
            onClick={() => setActiveModal('tenders')}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-gray-300 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-gray-100 text-gray-600 rounded-xl">
                <FileText className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList')}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('totalTendersNote') || 'Total Tenders & Proposals'}>{t('totalTenders') || 'Total Tenders/Proposals'}</p>
              <StatValue loading={kpiLoading}>{metrics.totalTenders?.toLocaleString() || 0}</StatValue>
            </div>
          </div>

          {/* Active Tenders */}
          <div
            onClick={() => setActiveModal('active-tenders')}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-amber-200 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-amber-100 text-amber-600 rounded-xl">
                <FileText className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList')}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('activeTendersNote') || 'Active Tenders & Proposals'}>{t('activeTenders') || 'Active Tenders/Proposals'}</p>
              <StatValue loading={kpiLoading}>{metrics.activeTenders?.toLocaleString() || 0}</StatValue>
            </div>
          </div>

          {/* Award Won Tenders */}
          <div
            onClick={() => setActiveModal('won-tenders')}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-emerald-200 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-emerald-100 text-emerald-600 rounded-xl">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList')}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('wonTendersNote') || 'Award Won Tenders & Proposals'}>{t('wonTenders') || 'Award Won Tenders/Proposals'}</p>
              <StatValue loading={kpiLoading}>{metrics.wonTenders?.toLocaleString() || 0}</StatValue>
            </div>
          </div>

          {/* Overdue Follow-ups */}
          <div
            onClick={() => { navigate('/follow-up'); }}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-red-200 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-red-100 text-red-600 rounded-xl">
                <AlertCircle className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList') || 'View List'}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('overdueFollowUps') || 'Overdue Follow-ups'}>{t('overdueFollowUps') || 'Overdue Follow-ups'}</p>
              <StatValue loading={leadsPending}>{followUpStats.overdue.toLocaleString()}</StatValue>
            </div>
          </div>

          {/* Today's Follow-ups */}
          <div
            onClick={() => { navigate('/follow-up'); }}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-blue-200 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
                <Clock className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList') || 'View List'}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('todayFollowUps') || 'Today\'s Follow-ups'}>{t('todayFollowUps') || 'Today\'s Follow-ups'}</p>
              <StatValue loading={leadsPending}>{followUpStats.today.toLocaleString()}</StatValue>
            </div>
          </div>

          {/* Upcoming Follow-ups */}
          <div
            onClick={() => { navigate('/follow-up'); }}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-slate-200 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-slate-100 text-slate-600 rounded-xl">
                <Calendar className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList') || 'View List'}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title={t('upcomingFollowUps') || 'Upcoming Follow-ups'}>{t('upcomingFollowUps') || 'Upcoming Follow-ups'}</p>
              <StatValue loading={leadsPending}>{followUpStats.upcoming.toLocaleString()}</StatValue>
            </div>
          </div>

        </div>
      </div>



      {/* Charts Row 1 - Stack on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue Trend */}
        <div className="lg:col-span-2 bg-white p-4 md:p-5 rounded-xl border shadow-sm">
          <div className="flex flex-row justify-between items-start mb-4 w-full gap-2">
            <h3 className="font-bold text-gray-800 flex items-start sm:items-center gap-2 text-sm md:text-base min-w-0">
              <BarChart3 className="w-4 h-4 shrink-0 mt-0.5 sm:mt-0" />
              <span className="leading-tight">{t('prequalTenderAwardTrend') || 'Prequalification, Tender & Award Won Trend'}</span>
            </h3>
            <div className="shrink-0">
              <Dropdown className="w-auto">
                <DropdownTrigger>
                  <button className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center gap-1 whitespace-nowrap">
                    {t('filterLines') || 'Filter Lines'}
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownTrigger>
                <DropdownPopover>
                  <DropdownMenu
                    className="w-56 p-1"
                    selectionMode="multiple"
                    selectedKeys={new Set(visibleTrendLines)}
                    onSelectionChange={(keys) => setVisibleTrendLines(Array.from(keys))}
                  >
                    {[
                      { id: 'prequalifications', label: t('prequalifications') || 'Prequalifications', color: 'text-blue-600' },
                      { id: 'enlisted', label: t('prequalEnlisted') || 'Prequalification Enlisted', color: 'text-purple-600' },
                      { id: 'tender', label: t('tendersProposals') || 'Tender/Proposal', color: 'text-amber-600' },
                      { id: 'booked', label: t('awardWon') || 'Award Won', color: 'text-emerald-600' }
                    ].map(line => (
                      <DropdownItem key={line.id} id={line.id}>
                        <span className={`font-medium ${line.color}`}>{line.label}</span>
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </DropdownPopover>
              </Dropdown>
            </div>
          </div>
          <div className="h-48 md:h-64">
            {kpiLoading ? (
              <div className="h-full flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : metrics.trendData && metrics.trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={metrics.trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={30} allowDecimals={false} domain={[0, 'auto']} />
                  <Tooltip formatter={(value, name) => [value.toLocaleString(), name]} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Area
                    type="monotone"
                    dataKey="prequalifications"
                    stroke="#3B82F6"
                    fill="#3B82F6"
                    fillOpacity={0.1}
                    name={t('prequalifications') || 'Prequalifications'}
                    hide={!visibleTrendLines.includes('prequalifications')}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="enlisted"
                    stroke="#8B5CF6"
                    fill="#8B5CF6"
                    fillOpacity={0.1}
                    name={t('prequalEnlisted') || 'Prequalification Enlisted'}
                    hide={!visibleTrendLines.includes('enlisted')}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="tender"
                    stroke="#F59E0B"
                    fill="#F59E0B"
                    fillOpacity={0.1}
                    name={t('tendersProposals') || 'Tender/Proposal'}
                    hide={!visibleTrendLines.includes('tender')}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="booked"
                    stroke="#10B981"
                    fill="#10B981"
                    fillOpacity={0.15}
                    name={t('awardWon') || 'Award Won'}
                    hide={!visibleTrendLines.includes('booked')}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <BarChart3 className="w-8 h-8 stroke-[1.5] mb-2 text-gray-300" />
                <p className="text-xs font-medium">{t('noDataAvailable') || 'No trend data available'}</p>
              </div>
            )}
          </div>
        </div>

        {/* Prequalification Sources */}
        <div className="bg-white p-4 md:p-5 rounded-xl border shadow-sm">
          <div className="flex flex-row justify-between items-start mb-4 w-full gap-2">
            <h3 className="font-bold text-gray-800 flex items-start sm:items-center gap-2 text-sm md:text-base min-w-0">
              <PieChart className="w-4 h-4 shrink-0 mt-0.5 sm:mt-0" />
              <span className="leading-tight">{t('trafficSources')}</span>
            </h3>
            <div className="shrink-0">
              <Dropdown>
                <DropdownTrigger>
                  <button className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center gap-1 whitespace-nowrap">
                    {typeFilter === 'All' ? t('all') || 'All' : typeFilter === 'Prequalification' ? t('prequalifications') : t('tendersProposals')}
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownTrigger>
                <DropdownPopover>
                  <DropdownMenu
                    selectedKeys={new Set([typeFilter])}
                    selectionMode="single"
                    onSelectionChange={(keys) => setTypeFilter(Array.from(keys)[0])}
                  >
                    <DropdownItem id="All">{t('all') || 'All'}</DropdownItem>
                    <DropdownItem id="Prequalification">{t('prequalifications')}</DropdownItem>
                    <DropdownItem id="Tender/Proposal">{t('tendersProposals')}</DropdownItem>
                  </DropdownMenu>
                </DropdownPopover>
              </Dropdown>
            </div>
          </div>
          <div className="h-36 md:h-48">
            {kpiLoading ? (
              <div className="h-full flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : sourceData && sourceData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={sourceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={55}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {sourceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value.toLocaleString()} ${typeFilter === 'All' ? t('records') || 'records' : (typeFilter === 'Prequalification' ? t('prequalifications') : t('tendersProposals'))}`, name]} />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <PieChart className="w-8 h-8 stroke-[1.5] mb-2 text-gray-300" />
                <p className="text-xs font-medium">{t('noDataAvailable') || 'No traffic source data available'}</p>
              </div>
            )}
          </div>
          {/* Legend below chart - clickable */}
          <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] md:text-xs">
            {sourceData.map((entry, index) => (
              <div
                key={entry.name}
                className="flex items-center gap-1 truncate cursor-pointer hover:bg-gray-100 p-1 rounded"
                onClick={() => { setSelectedSource(entry.name); setActiveModal('source'); }}
              >
                <span
                  className="w-2 h-2 md:w-3 md:h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="truncate text-gray-600">{entry.name === 'Unknown' ? t('unknownSource') : entry.name}</span>
                <span className="font-bold text-gray-800 ml-auto">
                  {periodSourceTotal > 0 ? ((entry.value / periodSourceTotal) * 100).toFixed(0) : 0}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* ── Sales Team Summary Card ─────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

        {/* ── Header ── */}
        <div className="p-4 md:p-5 border-b border-gray-100">
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">

            {/* Title */}
            <div className="flex items-center gap-3 shrink-0">
              <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl border border-blue-100 shrink-0">
                <BarChart3 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-base md:text-lg">{t('salesTeamSummary')}</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t('activityAndBookingsBetween') || 'Activity, bookings & new prequalifications within selected dates'}
                </p>
              </div>
            </div>

            {/* Controls: date filter + total badges */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto">

              {/* Date filter pills */}
              <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl shrink-0">
                {[
                  { label: t('todayBtn'), action: () => { const d = new Date(); const s = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; setSummaryFrom(s); setSummaryTo(s); } },
                  { label: t('thisMonth'), action: () => { const d = getMonthDefaults(); setSummaryFrom(d.from); setSummaryTo(d.to); } },
                  { label: t('thisYear'), action: () => { const y = new Date().getFullYear(); setSummaryFrom(`${y}-01-01`); setSummaryTo(`${y}-12-31`); } },
                  { label: t('allTime') || 'All', action: () => { setSummaryFrom('2000-01-01'); setSummaryTo(fmtDay(new Date())); } },
                  { label: t('customRange') || 'Custom', action: () => setShowCustomDate(!showCustomDate), active: showCustomDate },
                ].map(btn => (
                  <button key={btn.label} onClick={btn.action}
                    className={`text-[11px] font-bold px-3 py-1.5 rounded-lg transition-all whitespace-nowrap ${
                      btn.active ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-white'
                    }`}>
                    {btn.label}
                  </button>
                ))}
              </div>

              {/* Custom date pickers */}
              {showCustomDate && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <div className="w-[110px]">
                    <DatePicker value={summaryFrom} onChange={e => setSummaryFrom(e.target.value)} size="sm" />
                  </div>
                  <span className="text-gray-400 text-xs">→</span>
                  <div className="w-[110px]">
                    <DatePicker value={summaryTo} onChange={e => setSummaryTo(e.target.value)} size="sm" />
                  </div>
                </div>
              )}

              {/* Total stat badges */}
              <div className="flex gap-2 ml-auto sm:ml-0">
                <button
                  onClick={() => { setSummaryModalType('preq-total'); setVisibleCount(50); }}
                  className="flex items-center gap-2.5 bg-white border border-gray-200 hover:border-blue-300 hover:shadow-sm rounded-xl px-3.5 py-2 transition-all shrink-0 group"
                >
                  <div className="p-1.5 bg-blue-50 rounded-lg border border-blue-100 group-hover:bg-blue-100 transition-colors">
                    <BarChart3 className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-0.5">{t('total')} {t('prequalifications')}</div>
                    <div className="text-base font-black text-gray-900 leading-none">{crmSummary.salespeople.reduce((s, sp) => s + sp.preqTotal.length, 0)}</div>
                  </div>
                </button>
                <button
                  onClick={() => { setSummaryModalType('tp-total'); setVisibleCount(50); }}
                  className="flex items-center gap-2.5 bg-white border border-gray-200 hover:border-emerald-300 hover:shadow-sm rounded-xl px-3.5 py-2 transition-all shrink-0 group"
                >
                  <div className="p-1.5 bg-emerald-50 rounded-lg border border-emerald-100 group-hover:bg-emerald-100 transition-colors">
                    <BarChart3 className="w-3.5 h-3.5 text-emerald-600" />
                  </div>
                  <div className="text-left">
                    <div className="text-[9px] font-bold text-gray-400 uppercase tracking-widest leading-none mb-0.5">{t('total')} {t('proposalTender')}</div>
                    <div className="text-base font-black text-gray-900 leading-none">{crmSummary.salespeople.reduce((s, sp) => s + sp.tpTotal.length, 0)}</div>
                  </div>
                </button>
              </div>

            </div>
          </div>

          {/* Summary pills */}
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
            {[
              { label: `${t('prequalifications')} ${t('activeLabel')}`, val: crmSummary.salespeople.reduce((s, sp) => s + sp.preqActive.length, 0), color: 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100', type: 'preq-active' },
              { label: `${t('proposalTender')} ${t('activeLabel')}`, val: crmSummary.salespeople.reduce((s, sp) => s + sp.tpActive.length, 0), color: 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100', type: 'tp-active' },
              { label: `${t('proposalTender')} ${t('won')}`, val: crmSummary.salespeople.reduce((s, sp) => s + sp.won.length, 0), color: 'bg-green-50 text-green-600 border-green-200 hover:bg-green-100', type: 'stage-Won' },
              { label: `${t('followUpsGroup')} ${t('overdue')}`, val: crmSummary.overdueCallLeads.length, color: 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100', type: 'calls-overdue' },
              { label: `${t('followUpsGroup')} ${t('todayBtn')}`, val: crmSummary.todayCallLeads.length, color: 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100', type: 'calls-today' },
            ].map(pill => (
              <button key={pill.label} onClick={() => { setSummaryModalType(pill.type); setVisibleCount(50); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${pill.color}`}>
                {pill.label}: <span className="font-black">{pill.val}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Hierarchical Table ── */}
        {(() => {
          const getSp = (username) => crmSummary.salespeople.find(s => s.name === username) || null;
          const AVATAR_GRADIENTS = [
            'from-blue-500 to-indigo-600', 'from-emerald-500 to-teal-600',
            'from-amber-500 to-orange-600', 'from-purple-500 to-violet-600',
            'from-pink-500 to-rose-600', 'from-cyan-500 to-sky-600',
          ];
          const getGradient = (idx) => AVATAR_GRADIENTS[idx % AVATAR_GRADIENTS.length];
          const getInitials = (name) => (name || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

          const sumStats = (spList) => {
            const r = { preqTotal: 0, preqActive: 0, newLeads: 0, submitted: 0, pending: 0, enlisted: 0, disqualified: 0, tpTotal: 0, tpActive: 0, inProcess: 0, won: 0, lost: 0, hold: 0, callsOverdue: 0, callsToday: 0, callsUpcoming: 0, contractsExpiring: 0, contractsExpired: 0, contractsExtended: 0 };
            let tP = 0, tE = 0, tTp = 0, tW = 0;
            spList.forEach(sp => {
              if (!sp) return;
              r.preqTotal += sp.preqTotal.length; r.preqActive += sp.preqActive.length;
              r.newLeads += sp.newLeads.length; r.submitted += sp.submitted.length;
              r.pending += sp.pending.length; r.enlisted += sp.enlisted.length;
              r.disqualified += sp.disqualified.length; r.tpTotal += sp.tpTotal.length;
              r.tpActive += sp.tpActive.length; r.inProcess += sp.inProcess.length;
              r.won += sp.won.length; r.lost += sp.lost.length; r.hold += sp.hold.length;
              r.callsOverdue += sp.calls?.overdue?.length || 0;
              r.callsToday += sp.calls?.today?.length || 0;
              r.callsUpcoming += sp.calls?.upcoming?.length || 0;
              r.contractsExpiring += sp.contractsExpiring?.length || 0;
              r.contractsExpired += sp.contractsExpired?.length || 0;
              r.contractsExtended += sp.contractsExtended?.length || 0;
              tP += sp.preqTotal.length; tE += sp.enlisted.length;
              tTp += sp.tpTotal.length; tW += sp.won.length;
            });
            r.preqConvRate = tP > 0 ? Math.round((tE / tP) * 100) : 0;
            r.tpConvRate = tTp > 0 ? Math.round((tW / tTp) * 100) : 0;
            return r;
          };

          const convColor = (rate) => rate >= 25 ? 'text-emerald-600' : rate >= 10 ? 'text-amber-600' : 'text-red-400';
          const convBg = (rate) => rate >= 25 ? 'bg-emerald-100 text-emerald-600' : rate >= 10 ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-400';

          const StatCell = ({ val, cls = 'text-gray-700', onClick }) => onClick
            ? <button onClick={onClick} className={`text-sm font-bold px-1.5 py-0.5 rounded hover:opacity-80 transition-opacity ${cls}`}>{val}</button>
            : <span className={`text-sm font-bold ${cls}`}>{val}</span>;

          const CallCell = ({ val, cls }) => val > 0
            ? <span className={`inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full border ${cls}`}>{val}</span>
            : <span className="text-gray-200">—</span>;

          const renderDataRow = (username, rowIdx, indent = 0) => {
            const sp = getSp(username);
            const initials = getInitials(username);
            const gradient = getGradient(rowIdx);
            const openSp = (type) => { setSummaryModalType(`sp:${username}:${type}`); setVisibleCount(50); };
            const maxLeads = Math.max(...crmSummary.salespeople.map(s => s.total.length), 1);
            const barWidth = sp ? Math.round((sp.total.length / maxLeads) * 100) : 0;

            return (
              <motion.tr key={`row-${username}-${indent}`} className="group hover:bg-slate-50 transition-colors"
                initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: rowIdx * 0.05, duration: 0.3 }}>
                <td className="px-5 py-3 sticky left-0 bg-white z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] group-hover:bg-slate-50 transition-colors border-r border-gray-50">
                  <div className="flex items-center gap-3" style={{ paddingLeft: `${indent * 20}px` }}>
                    <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-xs font-black flex-shrink-0 shadow`}>
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-gray-900 text-sm truncate leading-tight">{username}</div>
                      {sp && (
                        <div className="mt-1 flex items-center gap-1.5">
                          <div className="flex-1 bg-gray-100 rounded-full h-1.5 w-20">
                            <div className={`h-full rounded-full bg-gradient-to-r ${gradient}`} style={{ width: `${barWidth}%` }} />
                          </div>
                          <span className="text-[10px] text-gray-400">{sp.preqTotal.length} {t('prequalifications')}{sp.tpTotal.length > 0 ? ` · ${sp.tpTotal.length} ${t('tenders')}` : ''}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                {sp ? (<>
                  {/* PREQUALIFICATIONS */}
                  <td className="px-3 py-3 text-center bg-blue-50/30 border-l border-blue-100"><button onClick={() => openSp('preqTotal')} className="text-sm font-black text-gray-800 hover:text-blue-600 px-2 py-0.5 rounded-lg hover:bg-blue-100 transition-colors">{sp.preqTotal.length}</button></td>
                  <td className="px-3 py-3 text-center bg-blue-50/30"><button onClick={() => openSp('preqActive')} className="text-sm font-bold text-blue-500 hover:bg-blue-100 px-2 py-0.5 rounded-lg transition-colors">{sp.preqActive.length}</button></td>
                  <td className="px-3 py-3 text-center bg-blue-50/30"><button onClick={() => openSp('new')} className="text-sm font-bold text-indigo-500 hover:bg-blue-100 px-2 py-0.5 rounded-lg transition-colors">{sp.newLeads.length}</button></td>
                  <td className="px-3 py-3 text-center bg-blue-50/30"><button onClick={() => openSp('submitted')} className="text-sm font-bold text-cyan-600 hover:bg-blue-100 px-2 py-0.5 rounded-lg transition-colors">{sp.submitted.length}</button></td>
                  <td className="px-3 py-3 text-center bg-blue-50/30"><button onClick={() => openSp('pending')} className="text-sm font-bold text-sky-600 hover:bg-blue-100 px-2 py-0.5 rounded-lg transition-colors">{sp.pending.length}</button></td>
                  <td className="px-3 py-3 text-center bg-blue-50/30"><button onClick={() => openSp('enlisted')} className="text-sm font-bold text-purple-600 hover:bg-blue-100 px-2 py-0.5 rounded-lg transition-colors">{sp.enlisted.length}</button></td>
                  <td className="px-3 py-3 text-center bg-blue-50/30"><button onClick={() => openSp('disqualified')} className="text-sm font-bold text-rose-500 hover:bg-blue-100 px-2 py-0.5 rounded-lg transition-colors">{sp.disqualified.length}</button></td>
                  <td className="px-3 py-3 text-center bg-blue-50/30 border-r border-blue-100">
                    <div className="flex flex-col items-center gap-1">
                      <span className={`text-[11px] font-black px-2 py-0.5 rounded-full ${sp.preqConvRate >= 25 ? 'bg-emerald-100 text-emerald-500' : sp.preqConvRate >= 10 ? 'bg-amber-100 text-amber-500' : 'bg-red-100 text-red-400'}`}>{sp.preqConvRate}%</span>
                    </div>
                  </td>
                  {/* TENDER / PROPOSAL */}
                  <td className="px-3 py-3 text-center bg-emerald-50/30 border-l border-emerald-100"><button onClick={() => openSp('tpTotal')} className="text-sm font-black text-gray-800 hover:text-emerald-600 px-2 py-0.5 rounded-lg hover:bg-emerald-100 transition-colors">{sp.tpTotal.length}</button></td>
                  <td className="px-3 py-3 text-center bg-emerald-50/30"><button onClick={() => openSp('tpActive')} className="text-sm font-bold text-emerald-500 hover:bg-emerald-100 px-2 py-0.5 rounded-lg transition-colors">{sp.tpActive.length}</button></td>
                  <td className="px-3 py-3 text-center bg-emerald-50/30"><button onClick={() => openSp('inProcess')} className="text-sm font-bold text-teal-600 hover:bg-emerald-100 px-2 py-0.5 rounded-lg transition-colors">{sp.inProcess.length}</button></td>
                  <td className="px-3 py-3 text-center bg-emerald-50/30"><button onClick={() => openSp('won')} className="text-sm font-bold text-green-500 hover:bg-emerald-100 px-2 py-0.5 rounded-lg transition-colors">{sp.won.length}</button></td>
                  <td className="px-3 py-3 text-center bg-emerald-50/30"><button onClick={() => openSp('lost')} className="text-sm font-bold text-red-500 hover:bg-emerald-100 px-2 py-0.5 rounded-lg transition-colors">{sp.lost.length}</button></td>
                  <td className="px-3 py-3 text-center bg-emerald-50/30"><button onClick={() => openSp('hold')} className="text-sm font-bold text-gray-600 hover:bg-emerald-100 px-2 py-0.5 rounded-lg transition-colors">{sp.hold.length}</button></td>
                  <td className="px-3 py-3 text-center bg-emerald-50/30 border-r border-emerald-100">
                    <div className="flex flex-col items-center gap-1">
                      <span className={`text-[11px] font-black px-2 py-0.5 rounded-full ${sp.tpConvRate >= 25 ? 'bg-emerald-100 text-emerald-500' : sp.tpConvRate >= 10 ? 'bg-amber-100 text-amber-500' : 'bg-red-100 text-red-400'}`}>{sp.tpConvRate}%</span>
                    </div>
                  </td>
                  {/* FOLLOW UPS */}
                  <td className="px-3 py-3 text-center border-l border-red-100 bg-red-50/30">
                    {sp.calls?.overdue?.length > 0 ? (
                      <button onClick={() => openSp('calls-overdue')} className="inline-flex items-center gap-1 text-xs font-bold text-red-400 bg-red-50 border border-red-200 hover:bg-red-100 px-2.5 py-1 rounded-full transition-colors">
                        <AlertTriangle className="w-3 h-3" />{sp.calls.overdue.length}
                      </button>
                    ) : <span className="text-gray-200">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center bg-red-50/30">
                    {sp.calls?.today?.length > 0 ? (
                      <button onClick={() => openSp('calls-today')} className="text-xs font-bold text-amber-400 bg-amber-50 border border-amber-200 hover:bg-amber-100 px-2.5 py-1 rounded-full transition-colors">{sp.calls.today.length}</button>
                    ) : <span className="text-gray-200">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center border-r border-red-100 bg-red-50/30">
                    {sp.calls?.upcoming?.length > 0 ? (
                      <button onClick={() => openSp('calls-upcoming')} className="text-xs font-bold text-emerald-400 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 px-2.5 py-1 rounded-full transition-colors">{sp.calls.upcoming.length}</button>
                    ) : <span className="text-gray-200">—</span>}
                  </td>
                  {/* CONTRACTS */}
                  <td className="px-3 py-3 text-center border-l border-fuchsia-100 bg-fuchsia-50/30">
                    {sp.contractsExpiring?.length > 0 ? <span className="text-xs font-bold text-amber-500 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">{sp.contractsExpiring.length}</span> : <span className="text-gray-200">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center bg-fuchsia-50/30">
                    {sp.contractsExpired?.length > 0 ? <span className="text-xs font-bold text-red-500 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">{sp.contractsExpired.length}</span> : <span className="text-gray-200">—</span>}
                  </td>
                  <td className="px-3 py-3 text-center border-r border-fuchsia-100 bg-fuchsia-50/30">
                    {sp.contractsExtended?.length > 0 ? <span className="text-xs font-bold text-emerald-500 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">{sp.contractsExtended.length}</span> : <span className="text-gray-200">—</span>}
                  </td>
                </>) : (
                  <td colSpan={21} className="px-3 py-3 text-center text-[11px] text-gray-300 italic">No activity in selected period</td>
                )}
              </motion.tr>
            );
          };

          // Group header row — used for both BM (indent=0) and TL (indent=1) rows
          const renderGroupHeader = (label, badge, badgeCls, totals, indent, isExpanded, onClick, gradient, associateCount, teamLeadCount) => (
            <tr key={`gh-${label}`} className={`${indent === 0 ? 'bg-slate-100 border-t-2 border-slate-300' : 'bg-indigo-50/60 border-t border-indigo-100'} cursor-pointer hover:brightness-95 transition-all`} onClick={onClick}>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} sticky left-0 z-10 ${indent === 0 ? 'bg-slate-100 hover:bg-slate-200' : 'bg-indigo-50/60 hover:bg-indigo-50'} border-r ${indent === 0 ? 'border-slate-200' : 'border-indigo-100'}`}>
                <div className="flex items-center gap-2.5" style={{ paddingLeft: `${indent * 20}px` }}>
                  <div className={`${indent === 0 ? 'w-9 h-9' : 'w-7 h-7'} rounded-xl bg-gradient-to-br ${indent === 0 ? 'from-slate-600 to-slate-800' : gradient} flex items-center justify-center text-white ${indent === 0 ? 'text-xs' : 'text-[10px]'} font-black flex-shrink-0 shadow`}>
                    {getInitials(label)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-black ${indent === 0 ? 'text-slate-800 text-sm' : 'text-indigo-700 text-[13px]'}`}>{label}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide whitespace-nowrap ${badgeCls}`}>{badge}</span>
                    </div>
                    <div className={`text-[10px] mt-0.5 ${indent === 0 ? 'text-slate-500' : 'text-indigo-400'}`}>
                      {teamLeadCount !== undefined && `${teamLeadCount} ${teamLeadCount !== 1 ? t('teamLeads') : t('teamLead')} · `}
                      {associateCount} {associateCount !== 1 ? t('associates') : t('associate')}
                    </div>
                  </div>
                  <ChevronDown className={`w-4 h-4 ${indent === 0 ? 'text-slate-400' : 'text-indigo-300'} transition-transform flex-shrink-0 ${isExpanded ? '' : '-rotate-90'}`} />
                </div>
              </td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-blue-50/70' : 'bg-blue-50/50'} border-l border-blue-100`}><span className="text-sm font-black text-gray-800">{totals.preqTotal}</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-blue-50/70' : 'bg-blue-50/50'}`}><span className="text-sm font-bold text-blue-500">{totals.preqActive}</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-blue-50/70' : 'bg-blue-50/50'}`}><span className="text-sm font-bold text-indigo-500">{totals.newLeads}</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-blue-50/70' : 'bg-blue-50/50'}`}><span className="text-sm font-bold text-cyan-600">{totals.submitted}</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-blue-50/70' : 'bg-blue-50/50'}`}><span className="text-sm font-bold text-sky-600">{totals.pending}</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-blue-50/70' : 'bg-blue-50/50'}`}><span className="text-sm font-bold text-purple-600">{totals.enlisted}</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-blue-50/70' : 'bg-blue-50/50'}`}><span className="text-sm font-bold text-rose-500">{totals.disqualified}</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-blue-50/70' : 'bg-blue-50/50'} border-r border-blue-100`}><span className={`text-[11px] font-black ${totals.preqConvRate >= 25 ? 'text-emerald-600' : totals.preqConvRate >= 10 ? 'text-amber-600' : 'text-red-400'}`}>{totals.preqConvRate}%</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-emerald-50/70' : 'bg-emerald-50/50'} border-l border-emerald-100`}><span className="text-sm font-black text-gray-800">{totals.tpTotal}</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-emerald-50/70' : 'bg-emerald-50/50'}`}><span className="text-sm font-bold text-emerald-500">{totals.tpActive}</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-emerald-50/70' : 'bg-emerald-50/50'}`}><span className="text-sm font-bold text-teal-600">{totals.inProcess}</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-emerald-50/70' : 'bg-emerald-50/50'}`}><span className="text-sm font-bold text-green-500">{totals.won}</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-emerald-50/70' : 'bg-emerald-50/50'}`}><span className="text-sm font-bold text-red-500">{totals.lost}</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-emerald-50/70' : 'bg-emerald-50/50'}`}><span className="text-sm font-bold text-gray-600">{totals.hold}</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center ${indent === 0 ? 'bg-emerald-50/70' : 'bg-emerald-50/50'} border-r border-emerald-100`}><span className={`text-[11px] font-black ${totals.tpConvRate >= 25 ? 'text-emerald-600' : totals.tpConvRate >= 10 ? 'text-amber-600' : 'text-red-400'}`}>{totals.tpConvRate}%</span></td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center border-l border-red-100 bg-red-50/30`}>{totals.callsOverdue > 0 ? <span className="text-xs font-bold text-red-400">{totals.callsOverdue}</span> : <span className="text-gray-300">0</span>}</td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center bg-red-50/30`}>{totals.callsToday > 0 ? <span className="text-xs font-bold text-amber-500">{totals.callsToday}</span> : <span className="text-gray-300">0</span>}</td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center border-r border-red-100 bg-red-50/30`}>{totals.callsUpcoming > 0 ? <span className="text-xs font-bold text-emerald-500">{totals.callsUpcoming}</span> : <span className="text-gray-300">0</span>}</td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center border-l border-fuchsia-100 bg-fuchsia-50/30`}>{totals.contractsExpiring > 0 ? <span className="text-xs font-bold text-amber-500">{totals.contractsExpiring}</span> : <span className="text-gray-300">—</span>}</td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center bg-fuchsia-50/30`}>{totals.contractsExpired > 0 ? <span className="text-xs font-bold text-red-500">{totals.contractsExpired}</span> : <span className="text-gray-300">—</span>}</td>
              <td className={`px-3 ${indent === 0 ? 'py-3' : 'py-2.5'} text-center border-r border-fuchsia-100 bg-fuchsia-50/30`}>{totals.contractsExtended > 0 ? <span className="text-xs font-bold text-emerald-500">{totals.contractsExtended}</span> : <span className="text-gray-300">—</span>}</td>
            </tr>
          );

          const buildRows = () => {
            const rows = [];
            let ri = 0;
            sectionKeysRef.current = new Set(); // reset and re-seed each render

            // Collect all users officially in the hierarchy tree to prevent them from dropping into "Unassigned" when their parent is collapsed
            const hierarchyUsernames = new Set();
            if (hierarchy.type === 'team_lead') {
              if (currentUser?.username) hierarchyUsernames.add(currentUser.username);
              (hierarchy.associates || []).forEach(a => hierarchyUsernames.add(a.username));
            } else if (hierarchy.type === 'bm') {
              if (currentUser?.username) hierarchyUsernames.add(currentUser.username);
              (hierarchy.teamLeads || []).forEach(tl => {
                hierarchyUsernames.add(tl.username);
                (tl.associates || []).forEach(a => hierarchyUsernames.add(a.username));
              });
            } else if (hierarchy.type === 'ceo') {
              (hierarchy.bms || []).forEach(bm => {
                hierarchyUsernames.add(bm.username);
                (bm.teamLeads || []).forEach(tl => {
                  hierarchyUsernames.add(tl.username);
                  (tl.associates || []).forEach(a => hierarchyUsernames.add(a.username));
                });
              });
              (hierarchy.orphanedTLs || []).forEach(tl => {
                hierarchyUsernames.add(tl.username);
                (tl.associates || []).forEach(a => hierarchyUsernames.add(a.username));
              });
            }

            if (hierarchy.type === 'team_lead') {
              // TEAM LEAD view: show TL own row first (if they have leads), then their associates
              const tlSelf = getSp(currentUser?.username);
              if (tlSelf) rows.push(renderDataRow(currentUser.username, ri++, 0));

              const associates = hierarchy.associates?.length > 0 ? hierarchy.associates : [];
              const usernames = associates.length > 0
                ? associates.map(a => a.username)
                : crmSummary.salespeople.filter(s => s.name !== currentUser?.username).map(s => s.name);
              usernames.forEach((name) => rows.push(renderDataRow(name, ri++, 0)));

            } else if (hierarchy.type === 'bm') {
              // BM view: show BM own row first (if they have leads), then TL groups
              const bmSelf = getSp(currentUser?.username);
              if (bmSelf) rows.push(renderDataRow(currentUser.username, ri++, 0));

              const tls = hierarchy.teamLeads || [];
              if (tls.length === 0) {
                crmSummary.salespeople
                  .filter(sp => sp.name !== currentUser?.username)
                  .forEach((sp) => rows.push(renderDataRow(sp.name, ri++, 0)));
              } else {
                tls.forEach((tl, tlIdx) => {
                  const key = `tl-${tl.id}`;
                  const expanded = expandedSections.has(key) || expandedSections.has('all');
                  const assocs = tl.associates || [];
                  // Include TL's own leads in their group totals
                  const tlSelf = getSp(tl.username);
                  const tlTotals = sumStats([tlSelf, ...assocs.map(a => getSp(a.username))].filter(Boolean));
                  sectionKeysRef.current.add(key);
                  const tlBadge = tl.role === 'Senior_Team_Lead' ? 'Senior Team Lead' : 'Team Lead';
                  const tlBadgeCls = tl.role === 'Senior_Team_Lead' ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-600';
                  rows.push(renderGroupHeader(tl.username, tlBadge, tlBadgeCls, tlTotals, 0, expanded, () => toggleSection(key), getGradient(tlIdx), assocs.length, undefined));
                  if (expanded) {
                    // TL's own row first (if they have direct leads)
                    if (tlSelf) rows.push(renderDataRow(tl.username, ri++, 1));
                    assocs.forEach(a => rows.push(renderDataRow(a.username, ri++, 1)));
                  }
                });
              }

            } else if (hierarchy.type === 'ceo') {
              // CEO view: GM groups -> TL sub-groups -> Associates
              const bms = hierarchy.bms || [];
              bms.forEach((bm, bmIdx) => {
                const bmKey = `bm-${bm.id}`;
                const bmExpanded = expandedSections.has(bmKey) || expandedSections.has('all');
                const allAssocs = (bm.teamLeads || []).flatMap(tl => tl.associates || []);
                const allTlSelfs = (bm.teamLeads || []).map(tl => getSp(tl.username));
                // Include GM's own leads in their group totals
                const bmSelf = getSp(bm.username);
                const bmTotals = sumStats([bmSelf, ...allTlSelfs, ...allAssocs.map(a => getSp(a.username))].filter(Boolean));
                sectionKeysRef.current.add(bmKey);
                rows.push(renderGroupHeader(bm.username, 'GM Operations', 'bg-slate-600 text-white', bmTotals, 0, bmExpanded, () => toggleSection(bmKey), getGradient(bmIdx), allAssocs.length, bm.teamLeads?.length || 0));

                if (bmExpanded) {
                  // GM's own row (if they handle leads directly)
                  if (bmSelf) rows.push(renderDataRow(bm.username, ri++, 1));

                  (bm.teamLeads || []).forEach((tl, tlIdx) => {
                    const tlKey = `tl-${tl.id}`;
                    const tlExpanded = expandedSections.has(tlKey) || expandedSections.has('all');
                    const tlAssocs = tl.associates || [];
                    const tlSelf = getSp(tl.username);
                    const tlTotals = sumStats([tlSelf, ...tlAssocs.map(a => getSp(a.username))].filter(Boolean));
                    sectionKeysRef.current.add(tlKey);
                    const subBadge = tl.role === 'Senior_Team_Lead' ? 'STL' : 'TL';
                    const subBadgeCls = tl.role === 'Senior_Team_Lead' ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-600';
                    rows.push(renderGroupHeader(tl.username, subBadge, subBadgeCls, tlTotals, 1, tlExpanded, () => toggleSection(tlKey), getGradient(tlIdx), tlAssocs.length, undefined));
                    if (tlExpanded) {
                      // TL's own row (if they handle leads directly)
                      if (tlSelf) rows.push(renderDataRow(tl.username, ri++, 2));
                      tlAssocs.forEach(a => rows.push(renderDataRow(a.username, ri++, 2)));
                    }
                  });
                }
              });

              // Orphaned TLs (not under any GM)
              const orphTLs = hierarchy.orphanedTLs || [];
              if (orphTLs.length > 0) {
                rows.push(<tr key="orphan-sep"><td colSpan={22} className="px-4 py-1.5 bg-gray-50"><span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Direct Team Leads</span></td></tr>);
                orphTLs.forEach((tl, tlIdx) => {
                  const key = `orphan-${tl.id}`;
                  const expanded = expandedSections.has(key) || expandedSections.has('all');
                  const assocs = tl.associates || [];
                  const tlSelf = getSp(tl.username);
                  const tlTotals = sumStats([tlSelf, ...assocs.map(a => getSp(a.username))].filter(Boolean));
                  sectionKeysRef.current.add(key);
                  const orphBadge = tl.role === 'Senior_Team_Lead' ? 'Senior Team Lead' : 'Team Lead';
                  const orphBadgeCls = tl.role === 'Senior_Team_Lead' ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-600';
                  rows.push(renderGroupHeader(tl.username, orphBadge, orphBadgeCls, tlTotals, 0, expanded, () => toggleSection(key), getGradient(tlIdx), assocs.length, undefined));
                  if (expanded) {
                    if (tlSelf) rows.push(renderDataRow(tl.username, ri++, 1));
                    assocs.forEach(a => rows.push(renderDataRow(a.username, ri++, 1)));
                  }
                });
              }

            } else {
              // Fallback: flat list
              crmSummary.salespeople.forEach((sp) => rows.push(renderDataRow(sp.name, ri++, 0)));
            }
            // Finally, find anyone in crmSummary.salespeople who isn't part of the hierarchy tree
            // This catches Unassigned leads or salespeople not properly mapped in the database
            const missingSp = crmSummary.salespeople.filter(sp => !hierarchyUsernames.has(sp.name));
            if (missingSp.length > 0) {
              rows.push(<tr key="other-sep"><td colSpan={22} className="px-4 py-1.5 bg-gray-50 border-t"><span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('otherUnassigned', { defaultValue: 'Other / Unassigned' })}</span></td></tr>);
              missingSp.forEach((sp, idx) => {
                rows.push(renderDataRow(sp.name, ri++, 0));
              });
            }

            return rows;
          };
          const grandTotals = sumStats(crmSummary.salespeople);

          return (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="text-[10px] font-black uppercase tracking-widest border-b">
                    <th className="px-4 py-2 text-left text-gray-400 w-56 sticky left-0 bg-white z-20">
                      {hierarchy.type === 'ceo' ? (t('organization') || 'Organization') : hierarchy.type === 'bm' ? (t('teamStructure') || 'Team Structure') : (t('members') || 'Members')}
                    </th>
                    <th colSpan={8} className="py-2 text-center text-blue-500 bg-blue-50 border-x border-blue-100">{t('prequalifications').toUpperCase()}</th>
                    <th colSpan={7} className="py-2 text-center text-emerald-600 bg-emerald-50 border-x border-emerald-100">{t('proposalTender').toUpperCase()}</th>
                    <th colSpan={3} className="py-2 text-center text-rose-500 bg-red-50 border-x border-red-100">{t('followUpsGroup', { defaultValue: 'FOLLOW UPS' })}</th>
                    <th colSpan={3} className="py-2 text-center text-fuchsia-600 bg-fuchsia-50 border-x border-fuchsia-100">{t('contractsGroup', { defaultValue: 'CONTRACTS' })}</th>
                  </tr>
                  <tr className="text-[11px] font-bold text-gray-500 border-b bg-gray-50">
                    <th className="px-4 py-2.5 text-left sticky left-0 bg-gray-50 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] border-r border-gray-100">
                      {hierarchy.type === 'ceo' ? (t('bmTlAssoc') || 'GM / Team Lead / Associate') : hierarchy.type === 'bm' ? (t('tlAssoc') || 'Team Lead / Associate') : t('salesperson')}
                    </th>
                    <th className="px-2 py-2.5 text-center bg-blue-50/60 border-l border-blue-100 text-gray-600">{t('total')}</th>
                    <th className="px-2 py-2.5 text-center bg-blue-50/60 text-blue-600">{t('activeLabel')}</th>
                    <th className="px-2 py-2.5 text-center bg-blue-50/60 text-indigo-500">{t('newLabel')}</th>
                    <th className="px-2 py-2.5 text-center bg-blue-50/60 text-cyan-600">{t('stageSubmitted')}</th>
                    <th className="px-2 py-2.5 text-center bg-blue-50/60 text-sky-600">{t('pending')}</th>
                    <th className="px-2 py-2.5 text-center bg-blue-50/60 text-purple-600">{t('stageEnlisted')}</th>
                    <th className="px-2 py-2.5 text-center bg-blue-50/60 text-rose-500">{t('stageDisqualified')}</th>
                    <th className="px-2 py-2.5 text-center bg-blue-50/60 border-r border-blue-100 text-indigo-600">{t('convRateAbbr')}</th>
                    <th className="px-2 py-2.5 text-center bg-emerald-50/60 border-l border-emerald-100 text-gray-600">{t('total')}</th>
                    <th className="px-2 py-2.5 text-center bg-emerald-50/60 text-emerald-600">{t('activeLabel')}</th>
                    <th className="px-2 py-2.5 text-center bg-emerald-50/60 text-teal-600">{t('stageInProcess')}</th>
                    <th className="px-2 py-2.5 text-center bg-emerald-50/60 text-green-600">{t('won')}</th>
                    <th className="px-2 py-2.5 text-center bg-emerald-50/60 text-red-500">{t('lost')}</th>
                    <th className="px-2 py-2.5 text-center bg-emerald-50/60 text-gray-600">{t('stageHold')}</th>
                    <th className="px-2 py-2.5 text-center bg-emerald-50/60 border-r border-emerald-100 text-indigo-600">{t('convRateAbbr')}</th>
                    <th className="px-2 py-2.5 text-center bg-red-50/60 border-l border-red-100 text-red-500">{t('overdue')}</th>
                    <th className="px-2 py-2.5 text-center bg-red-50/20 text-amber-600">{t('todayBtn')}</th>
                    <th className="px-2 py-2.5 text-center bg-red-50/20 border-r border-red-100 text-emerald-600">{t('upcoming')}</th>
                    <th className="px-2 py-2.5 text-center bg-fuchsia-50/60 border-l border-fuchsia-100 text-amber-600">{t('expiring45D', { defaultValue: 'Expiring 45D' })}</th>
                    <th className="px-2 py-2.5 text-center bg-fuchsia-50/60 text-red-500">{t('expired', { defaultValue: 'Expired' })}</th>
                    <th className="px-2 py-2.5 text-center bg-fuchsia-50/60 border-r border-fuchsia-100 text-emerald-600">{t('extended', { defaultValue: 'Extended' })}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {crmSummary.salespeople.length === 0 && (users || []).length === 0 ? (
                    <tr><td colSpan={22} className="px-5 py-12 text-center">
                      <div className="text-gray-300 mb-2 flex justify-center"><BarChart3 className="w-8 h-8 opacity-50" /></div>
                      <div className="text-gray-500 text-sm font-medium">{t('noLeadsSelectedPeriod')}</div>
                      <div className="text-gray-400 text-xs mt-1">{t('tryAdjustingDate')}</div>
                    </td></tr>
                  ) : buildRows()}
                </tbody>
                {crmSummary.salespeople.length > 0 && (
                  <tfoot>
                    <tr className="bg-gray-50 border-t-2 border-gray-200 text-gray-600">
                      <td className="px-4 py-3 font-black text-sm sticky left-0 bg-gray-50 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] border-r border-gray-200">{t('teamTotal')}</td>
                      <td className="px-2 py-3 text-center border-l border-gray-200"><span className="font-black text-gray-600">{grandTotals.preqTotal}</span></td>
                      <td className="px-2 py-3 text-center"><span className="font-black text-blue-500">{grandTotals.preqActive}</span></td>
                      <td className="px-2 py-3 text-center"><button onClick={() => { setSummaryModalType('stage-New'); setVisibleCount(50); }} className="font-black text-indigo-500 hover:text-indigo-600">{grandTotals.newLeads}</button></td>
                      <td className="px-2 py-3 text-center"><button onClick={() => { setSummaryModalType('stage-Submitted'); setVisibleCount(50); }} className="font-black text-cyan-600 hover:text-cyan-700">{grandTotals.submitted}</button></td>
                      <td className="px-2 py-3 text-center"><button onClick={() => { setSummaryModalType('stage-Pending'); setVisibleCount(50); }} className="font-black text-sky-600 hover:text-sky-700">{grandTotals.pending}</button></td>
                      <td className="px-2 py-3 text-center"><button onClick={() => { setSummaryModalType('stage-Enlisted'); setVisibleCount(50); }} className="font-black text-purple-600 hover:text-purple-700">{grandTotals.enlisted}</button></td>
                      <td className="px-2 py-3 text-center"><button onClick={() => { setSummaryModalType('stage-Disqualified'); setVisibleCount(50); }} className="font-black text-rose-500 hover:text-rose-600">{grandTotals.disqualified}</button></td>
                      <td className="px-2 py-3 text-center border-r border-gray-200"><span className={`font-black ${convColor(grandTotals.preqConvRate)}`}>{grandTotals.preqConvRate}%</span></td>
                      <td className="px-2 py-3 text-center border-l border-gray-200"><span className="font-black text-gray-600">{grandTotals.tpTotal}</span></td>
                      <td className="px-2 py-3 text-center"><span className="font-black text-emerald-500">{grandTotals.tpActive}</span></td>
                      <td className="px-2 py-3 text-center"><button onClick={() => { setSummaryModalType('stage-In Process'); setVisibleCount(50); }} className="font-black text-teal-600 hover:text-teal-700">{grandTotals.inProcess}</button></td>
                      <td className="px-2 py-3 text-center"><button onClick={() => { setSummaryModalType('stage-Won'); setVisibleCount(50); }} className="font-black text-green-500 hover:text-green-600">{grandTotals.won}</button></td>
                      <td className="px-2 py-3 text-center"><button onClick={() => { setSummaryModalType('stage-Lost'); setVisibleCount(50); }} className="font-black text-red-500 hover:text-red-600">{grandTotals.lost}</button></td>
                      <td className="px-2 py-3 text-center"><button onClick={() => { setSummaryModalType('stage-Hold'); setVisibleCount(50); }} className="font-black text-gray-600 hover:text-gray-700">{grandTotals.hold}</button></td>
                      <td className="px-2 py-3 text-center border-r border-gray-200"><span className={`font-black ${convColor(grandTotals.tpConvRate)}`}>{grandTotals.tpConvRate}%</span></td>
                      <td className="px-2 py-3 text-center border-l border-gray-200"><button onClick={() => { setSummaryModalType('calls-overdue'); setVisibleCount(50); }} className="font-bold text-red-400 hover:text-red-500">{grandTotals.callsOverdue}</button></td>
                      <td className="px-2 py-3 text-center"><button onClick={() => { setSummaryModalType('calls-today'); setVisibleCount(50); }} className="font-bold text-amber-400 hover:text-amber-500">{grandTotals.callsToday}</button></td>
                      <td className="px-2 py-3 text-center border-r border-gray-200"><button onClick={() => { setSummaryModalType('calls-upcoming'); setVisibleCount(50); }} className="font-bold text-emerald-400 hover:text-emerald-500">{grandTotals.callsUpcoming}</button></td>
                      <td className="px-2 py-3 text-center border-l border-gray-200"><span className="font-bold text-amber-500">{grandTotals.contractsExpiring}</span></td>
                      <td className="px-2 py-3 text-center"><span className="font-bold text-red-500">{grandTotals.contractsExpired}</span></td>
                      <td className="px-2 py-3 text-center border-r border-gray-200"><span className="font-bold text-emerald-500">{grandTotals.contractsExtended}</span></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          );
        })()}
      </div>





      {/* ── Why we lose ── */}
      <div className="bg-white p-4 md:p-5 rounded-xl border shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 gap-2">
          <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm md:text-base">
            <AlertTriangle className="w-4 h-4" /> {t('whyWeLose') || 'Why we lose'}
          </h3>
          <Dropdown className="w-auto">
            <DropdownTrigger>
              <button className="flex items-center gap-1 text-sm border-gray-300 border rounded-lg shadow-sm bg-white hover:bg-gray-50 focus:border-blue-500 focus:ring-blue-500 py-1.5 px-3">
                {lossViewType === 'prequalification' ? (t('prequalification') || 'Prequalification') : (t('tenderProposal') || 'Tender/Proposal')}
                <ChevronDown className="w-4 h-4 ml-1 text-gray-500" />
              </button>
            </DropdownTrigger>
            <DropdownPopover placement="bottom-end">
              <DropdownMenu
                selectedKeys={new Set([lossViewType])}
                onSelectionChange={(keys) => {
                  const arr = Array.from(keys);
                  if (arr.length > 0) setLossViewType(arr[0]);
                }}
                selectionMode="single"
              >
                <DropdownItem id="prequalification">{t('prequalification') || 'Prequalification'}</DropdownItem>
                <DropdownItem id="tender">{t('tenderProposal') || 'Tender/Proposal'}</DropdownItem>
              </DropdownMenu>
            </DropdownPopover>
          </Dropdown>
        </div>
        <p className="text-[11px] text-gray-400 mb-3">
          {lossViewType === 'prequalification'
            ? (t('whyWeDisqualifyNote') || 'Prequalifications created in the selected period that ended in Disqualified, by the reason recorded when they were closed')
            : (t('whyWeLoseNoteTp') || 'Tenders/Proposals created in the selected period that ended in Lost, by the reason recorded when they were closed')}
        </p>

        {/* The two win rates side by side — the gap between them is the cost
            of counting spam and silent numbers as sales failures. */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl border border-gray-200 bg-slate-50/60 p-3">
            <p className="text-[11px] text-gray-500 font-medium">
              {lossViewType === 'prequalification' ? (t('rawWinRatePreq') || 'Conversion rate, all inquiries') : (t('rawWinRateTp') || 'Win rate, all TP deals')}
            </p>
            {leadsPending
              ? <div className="h-7 flex items-center"><Skeleton width="55%" height={16} animation="wave" /></div>
              : <p className="text-lg font-bold text-gray-700 tabular-nums">{lossAnalysis.rawRate.toFixed(1)}%</p>}
          </div>
          <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3">
            <p className="text-[11px] text-blue-700 font-medium">
              {lossViewType === 'prequalification' ? (t('qualifiedWinRatePreq') || 'Conversion rate, qualified inquiries') : (t('qualifiedWinRate') || 'Win rate, qualified deals')}
            </p>
            {leadsPending ? (
              <div className="h-7 flex items-center"><Skeleton width="55%" height={16} animation="wave" /></div>
            ) : (
              <>
                <p className="text-lg font-bold text-blue-800 tabular-nums">
                  {lossAnalysis.recorded ? lossAnalysis.qualifiedRate.toFixed(1) + '%' : '—'}
                </p>
                <p className="text-[10px] text-blue-600/80 leading-tight mt-0.5">
                  {lossAnalysis.won} {t('wonShort') || 'won'} / {lossAnalysis.won + lossAnalysis.realLosses} {t('closedShort') || 'closed'}
                </p>
              </>
            )}
          </div>
        </div>

        {leadsPending ? (
          <CardLoading rows={3} />
        ) : lossAnalysis.rows.length === 0 ? (
          <div className="text-sm text-gray-400 py-3">
            {lossViewType === 'prequalification' ? (t('noLossReasons') || 'No loss reasons recorded yet — they are collected from now on, each time a prequalification is moved to Lost.') : (t('noLossReasonsTp') || 'No loss reasons recorded yet — they are collected from now on, each time a Tender/Proposal is moved to Lost.')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-gray-200 text-left">
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase">{t('reason') || 'Reason'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('prequalifications') || 'Prequalifications'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('shareOfLost') || 'Share'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lossAnalysis.rows.map((r) => (
                  <tr key={r.id} className={r.klass === 'disqualified' ? 'text-gray-400' : ''}>
                    <td className="px-3 py-2">
                      {t('loss_' + r.id) || r.label}
                      {r.klass === 'disqualified' && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">
                          {t('notADeal') || 'not a deal'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.count}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                      {pct(r.count, lossAnalysis.recorded)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {lossAnalysis.unrecorded > 0 && (
          <p className="text-[11px] text-amber-600 mt-2 leading-snug">
            {lossAnalysis.unrecorded} {lossViewType === 'prequalification' ? (t('lostWithoutReason') || 'prequalification(s) closed before reasons were collected — excluded from both the table and the qualified rate.') : (t('lostWithoutReasonTp') || 'tender/proposal(s) closed before reasons were collected — excluded from both the table and the qualified rate.')}
          </p>
        )}
      </div>

      <SpendEditor
        open={showSpendEditor}
        onClose={() => setShowSpendEditor(false)}
        ledger={adSpend}
        onSaved={(v) => { cachedAdSpend = v || { months: {} }; setAdSpend(cachedAdSpend); }}
      />







      {/* Alerts */}
      {
        metrics.overduePayments.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 md:p-4">
              <h3 className="font-bold text-red-800 flex items-center gap-2 mb-3 text-sm md:text-base">
                <AlertTriangle className="w-4 h-4" /> {t('overduePayments')} ({metrics.overduePayments.length})
              </h3>
              <div className="space-y-2 max-h-32 md:max-h-40 overflow-y-auto">
                {metrics.overduePayments.slice(0, 5).map((prequalification) => (
                  <div
                    key={prequalification.id}
                    className="flex justify-between items-center text-xs md:text-sm bg-white p-2 rounded"
                  >
                    <span className="font-medium truncate">{prequalification.clientName}</span>
                    <span className="text-red-600 ml-2 flex-shrink-0">{formatCurrency(prequalification.totalDue)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
}
